<template>
  <div
    class="online-duel-view"
    :class="[
      {
        compact: duelCompact,
        snug: duelSnug && !duelCompact,
        'picker-covers-rail': pickerCoversRail,
        'viewer-stowed': viewerStowed,
      },
      fieldAmbience ? `field-${fieldAmbience.key}` : null,
    ]"
    @pointerdown="primeSfx"
  >
    <aside v-if="!duelCompact && !pickerCoversRail" class="card-viewer-rail">
      <button type="button" class="sfx-toggle duel-leave" @click="goHome">
        {{ spectateLeaveLabel }}
      </button>
      <p v-if="isSpectate" class="watch-kicker">{{ t('duel.watching') }}</p>
      <p v-else-if="isReplay" class="watch-kicker">{{ t('duel.replay.kicker') }}</p>
      <button
        v-if="canSurrender"
        type="button"
        class="sfx-toggle duel-leave danger"
        @click="surrenderAsk = true"
      >
        {{ t('duel.surrender') }}
      </button>
      <button type="button" class="sfx-toggle" @click="toggleSfx">
        {{ sfxMuted ? t('duel.soundOff') : t('duel.soundOn') }}
      </button>
      <button type="button" class="sfx-toggle" @click="cycleAnimSpeed">
        {{ t(`duel.animSpeed${animSpeed}`) }}
      </button>
      <DesktopFullscreenButton tone="duel" />
      <ResponseModeChip
        v-if="state && !introLocked && !isSpectate && !isReplay"
        class="rail-response-chip"
        layout="chip"
        tone="duel"
      />
      <div
        v-if="spectatorNames.length"
        class="spectator-list"
        role="status"
        :aria-label="t('duel.spectatorsCount', { n: spectatorNames.length })"
      >
        <p class="spectator-list-title">{{ t('duel.spectators') }}</p>
        <ul>
          <li v-for="name in spectatorNames" :key="name">{{ name }}</li>
        </ul>
      </div>
      <div
        class="viewer-tabs"
        role="tablist"
        :aria-label="t('duel.viewerSettings')"
      >
        <button
          id="viewer-tab-card"
          type="button"
          role="tab"
          class="viewer-tab"
          :class="{ active: railTab === 'card' }"
          :aria-selected="railTab === 'card'"
          aria-controls="viewer-panel-card"
          @click="railTab = 'card'"
        >
          {{ t('duel.viewerCard') }}
        </button>
        <button
          v-if="!isSpectate"
          id="viewer-tab-settings"
          type="button"
          role="tab"
          class="viewer-tab"
          :class="{ active: railTab === 'settings' }"
          :aria-selected="railTab === 'settings'"
          aria-controls="viewer-panel-settings"
          @click="railTab = 'settings'"
        >
          {{ t('duel.viewerSettings') }}
        </button>
        <button
          id="viewer-tab-log"
          type="button"
          role="tab"
          class="viewer-tab"
          :class="{ active: railTab === 'log' }"
          :aria-selected="railTab === 'log'"
          aria-controls="viewer-panel-log"
          @click="railTab = 'log'"
        >
          {{ t('duel.viewerLog') }}
        </button>
      </div>
      <div
        id="viewer-panel-card"
        class="viewer-panel viewer-panel-card"
        role="tabpanel"
        aria-labelledby="viewer-tab-card"
        v-show="railTab === 'card'"
      >
        <CardViewer
          :card="hoveredCard"
          :faceDown="hoveredCardFaceDown"
          :sleeve-url="hoveredSleeve"
          :width="duelSnug && !duelCompact ? 200 : 260"
          :showInfo="true"
          :material-names="fusionMaterialNames"
        />
      </div>
      <div
        id="viewer-panel-settings"
        class="viewer-panel"
        role="tabpanel"
        aria-labelledby="viewer-tab-settings"
        v-show="railTab === 'settings'"
      >
        <DuelSettingsPanel v-if="!isSpectate" />
      </div>
      <div
        id="viewer-panel-log"
        class="viewer-panel viewer-panel-log"
        role="tabpanel"
        aria-labelledby="viewer-tab-log"
        v-show="railTab === 'log'"
      >
        <DuelEventLog :lines="eventLog" />
      </div>
      <div v-if="rankedCallVisible && !duelCompact" class="ranked-call-rail-slot">
        <RankedCallChip
          rail
          :pending="rankedCallPending"
          @duel="acceptRankedCall"
        />
      </div>
    </aside>

    <div
      ref="boardStage"
      class="board-stage"
      :class="{ 'duel-shaking': boardShaking, 'lp-anchored': lpAnchored }"
      :style="boardStageStyle"
    >
      <DuelFieldAtmosphere :ambience="fieldAmbience" />
      <button
        v-if="duelCompact"
        type="button"
        class="compact-gear compact-gear-corner"
        :title="gearOpen ? t('mobile.close') : t('duel.gear')"
        :aria-label="gearOpen ? t('mobile.close') : t('duel.gear')"
        :aria-expanded="gearOpen"
        @pointerdown.stop
        @touchstart.stop
        @mousedown.stop
        @click="gearOpen = !gearOpen"
      >
        <span v-if="gearOpen" class="compact-gear-x" aria-hidden="true">✕</span>
        <span v-else aria-hidden="true">⚙</span>
      </button>
      <div id="online-game-container" :class="{ 'game-container-blocked': anyOverlayOpen }">
        <div id="sharp-card-art" class="sharp-card-layer art" aria-hidden="true"></div>
        <div id="sharp-card-stats" class="sharp-card-layer stats" aria-hidden="true"></div>
        <output
          v-if="handWaitTop != null"
          class="hand-wait"
          :style="{ top: handWaitTop }"
        >{{ railNote('opp') }}</output>
      </div>
      <canvas ref="vfxCanvas" class="duel-vfx" aria-hidden="true"></canvas>
      <TournamentRoundClock
        v-if="roundEndsAt && state && !introLocked && !gameOverVisible"
        banner
        :ends-at="roundEndsAt"
      />

      <div
        v-if="state && !introLocked && !duelCompact"
        class="desktop-side-stack opp"
      >
        <div class="desktop-turn-dock">
          <TurnInfo
            docked
            :turnCount="turnCount"
            :currentPlayer="currentPlayer"
            :label="spectateTurnName"
          />
        </div>
        <div
          class="desktop-lp-avatar opp desktop-lp"
          :class="{ hit: lpLitOpp === 'hit', heal: lpLitOpp === 'heal' }"
        >
          <div class="desktop-lp-face">
            <AvatarView
              :config="oppAvatar"
              crop="bust"
              circle
              :size="railAvatarSize"
            />
            <RankBadge
              v-if="oppStanding"
              class="duel-seat-rank"
              :league="oppStanding.league"
              :division="oppStanding.division"
              :placement-remaining="oppStanding.placementRemaining"
              :size="40"
            />
            <DuelClockChip
              v-if="oppClock"
              :remaining-ms="oppClock.remainingMs"
              :running="oppClock.running"
              :at="oppClock.at"
            />
          </div>
          <div class="desktop-lp-score">
            <span class="compact-lp-cap">{{ vsRightName }}</span>
            <span class="compact-lp-stack">
              <span
                v-if="lpFloatOpp"
                :key="lpFloatOpp.id"
                class="compact-lp-delta"
                :class="{ hit: lpFloatOpp.hit, heal: !lpFloatOpp.hit }"
              >{{ lpFloatOpp.text }}</span>
              <span
                class="compact-lp-val"
                :class="{
                  punch: lpPunchOpp,
                  hit: lpLitOpp === 'hit',
                  heal: lpLitOpp === 'heal',
                }"
                :style="lpPunchOpp ? { '--lp-punch-ms': lpPunchOppMs + 'ms' } : undefined"
              >{{ shownOppLp }}</span>
            </span>
          </div>
        </div>
        <output v-if="railNote('opp')" class="seat-wait">{{ railNote('opp') }}</output>
      </div>
      <div
        v-if="state && !introLocked && !duelCompact"
        class="desktop-side-stack me"
      >
        <div
          class="desktop-lp-avatar me desktop-lp"
          :class="{ hit: lpLitMine === 'hit', heal: lpLitMine === 'heal' }"
        >
          <div class="desktop-lp-face">
            <AvatarView
              :config="myAvatar"
              crop="bust"
              circle
              :size="railAvatarSize"
            />
            <RankBadge
              v-if="myStanding"
              class="duel-seat-rank"
              :league="myStanding.league"
              :division="myStanding.division"
              :placement-remaining="myStanding.placementRemaining"
              :size="40"
            />
            <DuelClockChip
              v-if="myClock"
              :remaining-ms="myClock.remainingMs"
              :running="myClock.running"
              :at="myClock.at"
            />
          </div>
          <div class="desktop-lp-score">
            <span class="compact-lp-cap">{{ vsLeftName }}</span>
            <span class="compact-lp-stack">
              <span
                v-if="lpFloatMine"
                :key="lpFloatMine.id"
                class="compact-lp-delta"
                :class="{ hit: lpFloatMine.hit, heal: !lpFloatMine.hit }"
              >{{ lpFloatMine.text }}</span>
              <span
                class="compact-lp-val"
                :class="{
                  punch: lpPunchMine,
                  hit: lpLitMine === 'hit',
                  heal: lpLitMine === 'heal',
                }"
                :style="lpPunchMine ? { '--lp-punch-ms': lpPunchMineMs + 'ms' } : undefined"
              >{{ shownMyLp }}</span>
            </span>
          </div>
        </div>
        <output v-if="railNote('me')" class="seat-wait">{{ railNote('me') }}</output>
      </div>

      <div
        class="duel-hud-cluster"
        v-if="state && !introLocked && !duelCompact"
        :class="{ 'phase-control-locked': phaseClicksLocked }"
      >
        <PhaseControl
          :gameState="adapter"
          :revision="hudRevision"
          :compact="false"
          :watch="isSpectate || isReplay"
        />
      </div>

      <div
        v-if="duelCompact && gearOpen"
        class="compact-gear-backdrop"
        @pointerdown.stop
        @touchstart.stop
        @mousedown.stop
        @click="gearOpen = false"
      />
      <div
        v-if="duelCompact && gearOpen"
        class="compact-gear-panel"
        @pointerdown.stop
        @touchstart.stop
        @mousedown.stop
      >
        <button type="button" class="sfx-toggle" @click="goHome">
          {{ spectateLeaveLabel }}
        </button>
        <p v-if="isSpectate" class="watch-kicker">{{ t('duel.watching') }}</p>
        <p v-else-if="isReplay" class="watch-kicker">{{ t('duel.replay.kicker') }}</p>
        <button
          v-if="canSurrender"
          type="button"
          class="sfx-toggle danger"
          @click="surrenderAsk = true"
        >
          {{ t('duel.surrender') }}
        </button>
        <button type="button" class="sfx-toggle" @click="toggleSfx">
          {{ sfxMuted ? t('duel.soundOff') : t('duel.soundOn') }}
        </button>
        <button type="button" class="sfx-toggle" @click="cycleAnimSpeed">
          {{ t(`duel.animSpeed${animSpeed}`) }}
        </button>
        <DuelSettingsPanel v-if="!isSpectate" />
        <p class="compact-log-title">{{ t('duel.viewerLog') }}</p>
        <div class="compact-log-host">
          <DuelEventLog :lines="eventLog" />
        </div>
      </div>

      <PhaseBanner
        :phase="bannerPhase"
        :opponent="bannerOpponent"
        :quiet="promptOpen"
      />

      <RankedCallChip
        v-if="rankedCallVisible && duelCompact"
        :pending="rankedCallPending"
        @duel="acceptRankedCall"
      />

      <DuelPromptOverlay
        :message="seatWait ? '' : promptMessage"
        :toast="toastMessage"
        :toast-tone="toastTone"
        :actions="promptActions"
        :question-id="trapQuestionId"
        :anchor="menuAnchor"
        :compact="duelCompact"
        :place="promptPlace"
        :split="promptActionsSplit"
        @action="onPromptAction"
        @dismiss="onPromptAction('menu:cancel')"
      />

      <CardPickerOverlay
        :visible="!!cardPicker"
        :title="cardPicker?.title ?? ''"
        :items="pickerItems"
        :min="cardPicker?.min ?? 1"
        :max="cardPicker?.max ?? 1"
        :view-only="!!cardPicker?.viewOnly"
        :hint="cardPicker?.hint"
        :source="pickerSource"
        @hover="onPickerHover"
        @pick="onPickerPick"
        @confirm="onPickerConfirm"
        @cancel="onPickerCancel"
      />

      <!-- Centered on the board column (not the whole screen / side rails). -->
      <div
        v-if="activationCardId && !cardPicker && !positionVisible"
        class="activation-overlay"
        :class="[
          {
            'activation-trap': activationKind === 'trap',
            'activation-summon': activationKind === 'summon',
          },
          `activation-foil-${activationFoil}`,
        ]"
      >
        <div class="activation-card-wrap">
          <CardArt
            v-if="activationCardId"
            class="activation-card"
            :card-id="activationCardId"
            :foil="activationFoil"
            intensity="full"
            alt="Activated card"
          />
          <div class="activation-destello" aria-hidden="true">
            <span class="activation-destello-star" />
            <span class="activation-destello-star diag" />
          </div>
          <div
            v-if="activationTurnsMax > 0"
            class="activation-turn-counter"
          >
            <span
              :key="activationTurnShown"
              class="activation-turn-digit"
              >{{ activationTurnShown }}</span>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="duelCompact && state && !introLocked"
      class="compact-focus-rail"
    >
      <div class="compact-lp" aria-live="polite">
        <div
          class="compact-lp-opp"
          :class="{ hit: lpLitOpp === 'hit', heal: lpLitOpp === 'heal' }"
        >
          <AvatarView
            :config="oppAvatar"
            crop="bust"
            circle
            :size="28"
          />
          <RankBadge
            v-if="oppStanding && showCompactRank"
            class="duel-seat-rank compact"
            :league="oppStanding.league"
            :division="oppStanding.division"
            :placement-remaining="oppStanding.placementRemaining"
            :size="32"
          />
          <span class="compact-opp-line">
            <span class="compact-lp-cap">{{ vsRightName }}</span>
            <DuelClockChip
              v-if="oppClock"
              :remaining-ms="oppClock.remainingMs"
              :running="oppClock.running"
              :at="oppClock.at"
            />
            <span
              class="compact-turn"
              :class="{ opponent: bannerOpponent }"
              :title="compactTurnTitle"
            >{{ turnCount }}</span>
          </span>
          <span class="compact-lp-stack">
            <span
              v-if="lpFloatOpp"
              :key="lpFloatOpp.id"
              class="compact-lp-delta"
              :class="{ hit: lpFloatOpp.hit, heal: !lpFloatOpp.hit }"
            >{{ lpFloatOpp.text }}</span>
            <span
              class="compact-lp-val"
              :class="{
                punch: lpPunchOpp,
                hit: lpLitOpp === 'hit',
                heal: lpLitOpp === 'heal',
              }"
              :style="lpPunchOpp ? { '--lp-punch-ms': lpPunchOppMs + 'ms' } : undefined"
            >{{ shownOppLp }}</span>
          </span>
          <output v-if="railNote('opp')" class="seat-wait">{{ railNote('opp') }}</output>
        </div>
        <div
          class="compact-lp-me"
          :class="{ hit: lpLitMine === 'hit', heal: lpLitMine === 'heal' }"
        >
          <AvatarView
            :config="myAvatar"
            crop="bust"
            circle
            :size="28"
          />
          <RankBadge
            v-if="myStanding && showCompactRank"
            class="duel-seat-rank compact"
            :league="myStanding.league"
            :division="myStanding.division"
            :placement-remaining="myStanding.placementRemaining"
            :size="32"
          />
          <span class="compact-me-line">
            <span class="compact-lp-cap">{{ vsLeftName }}</span>
            <DuelClockChip
              v-if="myClock"
              :remaining-ms="myClock.remainingMs"
              :running="myClock.running"
              :at="myClock.at"
            />
          </span>
          <span class="compact-lp-stack">
            <span
              v-if="lpFloatMine"
              :key="lpFloatMine.id"
              class="compact-lp-delta"
              :class="{ hit: lpFloatMine.hit, heal: !lpFloatMine.hit }"
            >{{ lpFloatMine.text }}</span>
            <span
              class="compact-lp-val"
              :class="{
                punch: lpPunchMine,
                hit: lpLitMine === 'hit',
                heal: lpLitMine === 'heal',
              }"
              :style="lpPunchMine ? { '--lp-punch-ms': lpPunchMineMs + 'ms' } : undefined"
            >{{ shownMyLp }}</span>
          </span>
          <output v-if="railNote('me')" class="seat-wait">{{ railNote('me') }}</output>
        </div>
      </div>
      <div :class="{ 'phase-control-locked': phaseClicksLocked }">
        <PhaseControl
          :gameState="adapter"
          :revision="hudRevision"
          compact
          :watch="isSpectate || isReplay"
        />
      </div>
      <div v-if="duelPrefs.focusBands" class="focus-arrows">
        <button
          type="button"
          class="focus-arrow"
          :title="t('duel.focusCycle')"
          :aria-label="t('duel.focusCycle')"
          @click="shiftFocus(1)"
        >
          {{ focusBand === 'hand' ? '▲' : '▼' }}
        </button>
      </div>
    </div>

    <ReplayBar
      v-if="isReplay && state && !gameOverVisible"
      :playing="replayPlaying"
      :speed="replaySpeed"
      @toggle="toggleReplay"
      @step="stepReplay"
      @cycle-speed="cycleReplaySpeed"
    />

    <output
      v-if="linkDown && state && !sessionYielded && !roomLost && !gameOverVisible"
      class="link-banner"
    >
      {{ t('duel.reconnecting') }}
    </output>
    <output
      v-else-if="oppLinkDown && state && !sessionYielded && !roomLost && !gameOverVisible"
      class="link-banner"
    >
      {{ t('duel.opponentReconnecting') }}
    </output>

    <!-- Connection / lobby overlay (hidden once the match state arrives) -->
    <div
      class="online-overlay"
      :class="{ 'online-overlay-queue': queueSearching }"
      v-if="statusMessage && (!state || sessionYielded || roomLost)"
    >
      <div class="online-overlay-body">
        <div class="online-status">{{ tournamentOverlayTitle || statusMessage }}</div>
        <p v-if="tournamentCountdown" class="tournament-clock" role="timer">
          {{ tournamentCountdown }}
        </p>
        <QueueWaitPanel
          v-if="queueSearching"
          :elapsed-sec="queueElapsedSec"
          :elapsed-label="queueElapsedLabel"
        />
        <div v-if="showRoomCode" class="online-code">
          {{ t('duel.roomCode') }} <strong>{{ roomCode }}</strong>
        </div>
        <button type="button" class="online-back" @click="goHome">
          {{ overlayLeaveLabel }}
        </button>
      </div>
    </div>

    <PvpBanlistDialog
      v-if="banlistDialogOpen"
      :violations="banlistViolations"
      @close="closeBanlistDialog"
      @edit="editDeckFromBanlist"
    />

    <GraveyardOverlay
      :visible="graveyardVisible"
      :cards="graveyardCards"
      :owner="graveyardOwner"
      @close="graveyardVisible = false"
      @hover="onOverlayHover"
    />

    <GraveyardOverlay
      :visible="extraVisible"
      :cards="extraCards"
      :owner="extraOwner"
      :customTitle="extraOwner === 'player' ? t('duel.extraYours') : t('duel.extraOpp')"
      :available-card-names="fusionMaterialNames"
      @close="extraVisible = false"
      @hover="onOverlayHover"
    />

    <GraveyardOverlay
      :visible="banishedVisible"
      :cards="banishedCards"
      :owner="banishedOwner"
      :customTitle="banishedOwner === 'player' ? t('duel.banishedYours') : t('duel.banishedOpp')"
      @close="banishedVisible = false"
      @hover="onOverlayHover"
    />

    <GameOverOverlay
      :visible="gameOverVisible"
      :winner="gameOverWinner"
      :win-reason="gameOverReason"
      :headline="gameOverHeadline"
      :detail="gameOverDetail"
      :hide-replay="isSpectate || isReplay"
      :leave-label="spectateFromTournament ? spectateLeaveLabel : undefined"
      :left-avatar="myAvatar"
      :right-avatar="oppAvatar"
      :reward-delta="gameOverReward"
      :nudge-coins="gameOverNudge"
      :ranked-offer-coins="gameOverOfferCoins"
      :ranked="gameOverRanked"
      :progress="gameOverProgress"
      :tournament="isTournament"
      @return-to-lobby="goHome"
      @play-again="playAgain"
      @play-ranked="playRankedOffer"
    />

    <ExodiaOverlay
      v-if="exodiaVisible"
      :won="gameOverWinner === 'player'"
      @done="onExodiaDone"
    />

    <div
      v-if="surrenderAsk || leaveAsk"
      class="surrender-overlay"
      @touchstart.stop
      @mousedown.stop
    >
      <div class="surrender-panel">
        <p>
          {{ leaveAsk ? t('duel.leaveConfirm') : t('duel.surrenderConfirm') }}
        </p>
        <div class="surrender-actions">
          <button
            type="button"
            class="sfx-toggle danger"
            @click="leaveAsk ? confirmLeave() : confirmSurrender()"
          >
            {{ leaveAsk ? t('duel.back') : t('duel.surrender') }}
          </button>
          <button
            type="button"
            class="sfx-toggle"
            @click="surrenderAsk = false; leaveAsk = false"
          >
            {{ t('duel.surrenderCancel') }}
          </button>
        </div>
      </div>
    </div>

    <VsAiStartSheet
      :visible="aiPickVisible"
      @cancel="goHome"
      @start="confirmAiDifficulty"
    />

    <DuelVsOverlay
      v-if="vsVisible"
      :left-name="vsLeftName"
      :right-name="vsRightName"
      :left-avatar="myAvatar"
      :right-avatar="oppAvatar"
      @done="onVsDone"
    />

    <DuelRosterOverlay
      v-if="rosterVisible && rosterYou && rosterOpp"
      :you-name="vsLeftName"
      :opp-name="vsRightName"
      :you-avatar="myAvatar"
      :opp-avatar="oppAvatar"
      :you="rosterYou"
      :opp="rosterOpp"
      @done="onRosterDone"
    />

    <DiceOffOverlay
      v-if="diceVisible"
      :left-label="diceLeftLabel"
      :right-label="diceRightLabel"
      :left-avatar="myAvatar"
      :right-avatar="oppAvatar"
      :preset="dicePreset"
      :viewer-seat="mySeat"
      @done="onDiceDone"
      @cancel="goHome"
    />

    <PositionSelectionOverlay
      :visible="positionVisible"
      :card="positionCard"
      mode="position"
      @select="onPositionSelected"
      @cancel="onPositionCancel"
    />

    <aside
      v-if="duelCompact && !pickerCoversRail"
      class="card-viewer-text-rail"
      :class="{ stowed: viewerStowed }"
    >
      <div
        class="viewer-thumb-tools"
        @pointerdown.stop
        @touchstart.stop
        @mousedown.stop
      >
        <button
          type="button"
          class="compact-gear"
          :title="viewerStowed ? t('duel.showViewer') : t('duel.hideViewer')"
          :aria-label="viewerStowed ? t('duel.showViewer') : t('duel.hideViewer')"
          :aria-pressed="viewerStowed"
          @click="duelPrefs.setHideCardViewer(!duelPrefs.hideCardViewer)"
        >
          <span aria-hidden="true">{{ viewerStowed ? '◀' : '▶' }}</span>
        </button>
        <span
          v-if="spectatorNames.length"
          class="spectator-eye"
          role="status"
          :aria-label="t('duel.spectatorsCount', { n: spectatorNames.length })"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <path
              d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linejoin="round"
            />
            <circle cx="12" cy="12" r="2.5" fill="currentColor" />
          </svg>
          <span aria-hidden="true">{{ spectatorNames.length }}</span>
        </span>
        <button
          v-if="state && !introLocked && !isSpectate && !isReplay"
          type="button"
          class="compact-gear"
          :disabled="!canShuffleHand"
          :title="t('duel.shuffleHand')"
          :aria-label="t('duel.shuffleHand')"
          @click="shuffleHand"
        >
          <span class="shuffle-hand-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
              <path
                d="M16 3h5v5M4 20 21 3M21 16v5h-5M15 15l6 6M4 4l5 5"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        </button>
        <ResponseModeChip
          v-if="state && !introLocked && !isSpectate && !isReplay"
          class="compact-trap-chip"
          layout="chip"
          tone="duel"
        />
        <button
          v-if="selectingTarget"
          type="button"
          class="compact-cancel"
          @click="cancelBoardSelection"
        >
          {{ t('duel.prompt.cancel') }}
        </button>
      </div>
      <CardViewer
        v-if="!viewerStowed"
        :card="hoveredCard"
        :faceDown="hoveredCardFaceDown"
        :sleeve-url="hoveredSleeve"
        fluid
        :width="168"
        :showImage="true"
        :showInfo="true"
        :material-names="fusionMaterialNames"
      />
    </aside>

    <button
      v-if="state && !introLocked && !duelCompact && !isSpectate && !isReplay"
      type="button"
      class="duel-shuffle-hand"
      :disabled="!canShuffleHand"
      :title="t('duel.shuffleHand')"
      :aria-label="t('duel.shuffleHand')"
      @click="shuffleHand"
    >
      <span class="shuffle-hand-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
          <path
            d="M16 3h5v5M4 20 21 3M21 16v5h-5M15 15l6 6M4 4l5 5"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
      {{ t('duel.shuffleHand') }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, markRaw, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import QueueWaitPanel from '../components/QueueWaitPanel.vue'
import Phaser from 'phaser'
import { Player } from '../types'
import {
  ActionType,
  AI_DUEL_MAX_MS,
  CardPosition,
  type DuelReplay,
  EnginePhase,
  parseAiDeckDifficulty,
  REPLAY_ENGINE_VERSION,
  isPlacement,
  parseAvatarConfig,
  pickCpuAvatar,
  type AvatarConfig,
  type DiceOffResult,
  type GameOverPayload,
  type MatchState,
  type RankedDelta,
  type RankedStanding,
  type DuelXpResult,
  type GameEvent,
  type Seat,
  type DuelClockPayload,
  type DuelRosterCard,
  type TournamentMatchView,
  duelActionCard,
  duelPendingCard,
  duelPendingKind,
  pvpTempBanlistViolations,
  type BanlistViolation,
} from '@shared/index'
import {
  NetworkDuelScene,
  type CardPickerRequest,
  type CompactLpTick,
  type DuelPickSide,
  type FieldMenuPayload,
  type FocusBand,
} from '../game/scenes/NetworkDuelScene'
import { NetworkGameClient, type ConnectMode } from '../game/net/NetworkGameClient'
import { SpectateGameClient } from '../game/net/SpectateGameClient'
import { LocalEngineClient } from '../game/net/LocalEngineClient'
import { ReplayClient } from '../game/net/ReplayClient'
import { api } from '../api/http'
import { fetchReplay, postAiReplay } from '../api/profile'
import { adminClientPath, readAdminKey } from './admin/path'
import { useAuthStore } from '../stores/auth'
import { useTournamentStore } from '../stores/tournament'
import { openTournamentMatch } from '../tournament/go'
import {
  clearLiveAiDuel,
  noteLiveAiDuel,
  setLiveAiKillHandler,
  type LiveAiKill,
} from '../game/net/reportLiveDuel'
import {
  dropRankedCall,
  rankedCallSkip,
  releaseRankedClaim,
  requestRankedCall,
  stageRankedCall,
  takeStagedRankedCall,
} from '../game/net/rankedCall'
import { pullInterruptNotice } from '../account/interruptNotice'
import { accountRename } from '../account/liveRename'
import { chatNamesMatch } from '@shared/index'
import { deckSearchPrompt } from '../game/deckSearchPrompt'
import { NetworkPhaseAdapter } from '../game/net/NetworkPhaseAdapter'
import type { GameClient } from '../game/net/GameClient'
import TurnInfo from '../components/TurnInfo.vue'
import PhaseControl from '../components/PhaseControl.vue'
import PhaseBanner from '../components/PhaseBanner.vue'
import RankedCallChip from '../components/RankedCallChip.vue'
import ResponseModeChip from '../components/ResponseModeChip.vue'
import DuelFieldAtmosphere from '../components/DuelFieldAtmosphere.vue'
import CardViewer from '../components/CardViewer.vue'
import type { CardViewerData } from '../components/CardViewer.vue'
import DuelSettingsPanel from '../components/DuelSettingsPanel.vue'
import DuelEventLog from '../components/DuelEventLog.vue'
import GraveyardOverlay from '../components/GraveyardOverlay.vue'
import GameOverOverlay from '../components/GameOverOverlay.vue'
import ReplayBar from '../components/ReplayBar.vue'
import ExodiaOverlay from '../components/ExodiaOverlay.vue'
import DiceOffOverlay from '../components/DiceOffOverlay.vue'
import DuelVsOverlay from '../components/DuelVsOverlay.vue'
import DuelRosterOverlay from '../components/DuelRosterOverlay.vue'
import VsAiStartSheet from '../components/VsAiStartSheet.vue'
import AvatarView from '../components/AvatarView.vue'
import RankBadge from '../components/RankBadge.vue'
import DuelClockChip from '../components/DuelClockChip.vue'
import TournamentRoundClock from '../components/TournamentRoundClock.vue'
import PositionSelectionOverlay from '../components/PositionSelectionOverlay.vue'
import DuelPromptOverlay, {
  type DuelPromptAction,
  type DuelToastTone,
} from '../components/DuelPromptOverlay.vue'
import DesktopFullscreenButton from '../components/DesktopFullscreenButton.vue'
import CardPickerOverlay, { type CardPickerItem } from '../components/CardPickerOverlay.vue'
import CardArt from '../components/CardArt.vue'
import PvpBanlistDialog from '../components/PvpBanlistDialog.vue'
import { DuelSfx } from '../game/audio/DuelSfx'
import { AnimationSpeed } from '../game/AnimationSpeed'
import { bindDuelVfx, parkDuelVfx, unbindDuelVfx } from '../game/vfx/DuelVfx'
import { activeFieldAmbience } from '../game/fieldAmbience'
import { ensurePlayableDeckList, eventOwnedDeckFits } from '../game/localDeck'
import { useI18n } from '../i18n/t'
import { localizedName } from '../i18n/localizeCard'
import { getCatalogCard } from '../game/packs/packCatalog'
import { getLocalCardImageUrl } from '../utils/cardImages'
import { localizeEngineError } from '../i18n/engineError'
import { usePlayerStore } from '../stores/player'
import { useChatStore } from '../stores/chat'
import { useDuelPrefsStore } from '../stores/duelPrefs'
import { useWalletStore } from '../stores/wallet'
import { useProgressStore } from '../stores/progress'
import { useCollectionStore } from '../stores/collection'
import { useMissionsStore } from '../stores/missions'
import { useLastCallStore } from '../stores/lastCall'
import { useLeagueRewardsStore } from '../stores/leagueRewards'
import { useRankedLobbyStore } from '../stores/rankedLobby'
import { useSiteStore } from '../stores/site'
import { noteAiDeck } from '../analytics/deckUsage'
import { track } from '../analytics/track'
import {
  detectBattleStall,
  DuelStallWatch,
  isWaitingOnOpponent,
  STALL_QUIET_MS,
  stallQuietLimit,
  type StallDebug,
} from '../game/duelStall'
import {
  CardFaultWatch,
  detectCardFaults,
  offeringHandFault,
  type CardFaultHit,
} from '../game/cardFault'
import { useCompactLayout } from '../composables/useCompactLayout'
import {
  duelLogLinesFromEvents,
  type DuelLogLine,
} from '../game/duelEventLog'

const { t } = useI18n()
const { duelCompact, duelSnug, needsRotate } = useCompactLayout()
const railAvatarSize = computed(() => (duelSnug.value && !duelCompact.value ? 72 : 120))
const player = usePlayerStore()
const chatUi = useChatStore()
const duelPrefs = useDuelPrefsStore()
const wallet = useWalletStore()
const rankedLobby = useRankedLobbyStore()
const railTab = ref<'card' | 'settings' | 'log'>('card')
const spectatorNames = ref<string[]>([])
const gearOpen = ref(false)
const focusBand = ref<FocusBand>('hand')

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const isSpectate = computed(() => String(route.query.mode || '') === 'spectate')
const isReplay = computed(() => String(route.query.mode || '') === 'replay')
const isTournament = computed(() => String(route.query.mode || '') === 'tournament')
const replaySpeed = ref<1 | 2 | 4>(1)
const replayPlaying = ref(true)
let replayClient: ReplayClient | null = null
const spectateFromOps = computed(
  () => isSpectate.value && String(route.query.from || '') === 'ops',
)
const spectateFromTournament = computed(
  () => isSpectate.value && String(route.query.from || '') === 'tournament',
)
const spectateLeaveLabel = computed(() => {
  if (!isSpectate.value) return t('duel.back')
  if (spectateFromOps.value) return t('duel.watchBack')
  if (spectateFromTournament.value) return t('duel.backHome')
  return t('duel.watchBackList')
})

let game: Phaser.Game | null = null
/** The board is Canvas. A lost WebGL canvas is what paints the white field. */
let phaserType: number = Phaser.CANVAS
let boardPaintNoted = false
let boardPaintTimer: ReturnType<typeof setTimeout> | null = null
/**
 * Dice and the VS intro each open their own WebGL context. On a fresh
 * ranked match that steals the board context; a private room has already
 * been drawing, so the same intro leaves its canvas alone.
 */
let introGlLost = false

/** Minimal redraws the board at 30 fps. Full and Reduced stay on the display rate. */
function applyBoardFps(): void {
  if (!game?.loop?.running) return
  const minimal = duelPrefs.motionLevel === 'minimal'
  const loop = game.loop
  loop.targetFps = minimal ? 30 : 60
  loop.fpsLimit = minimal ? 30 : 0
  loop.hasFpsLimit = minimal
  const paced = loop as Phaser.Core.TimeStep & {
    _limitRate: number
    _target: number
  }
  paced._limitRate = minimal ? 1000 / 30 : 0
  paced._target = 1000 / loop.targetFps
  loop.sleep()
  loop.wake(true)
}

watch(
  () => duelPrefs.motionLevel,
  () => applyBoardFps(),
)

let client: GameClient | null = null
let matchStarted = false
let matchFinished = false
let matchBeganAt = 0
const stallWatch = new DuelStallWatch()
const faultWatch = new CardFaultWatch()
let queueBeganAt = 0
let scaleObserver: ResizeObserver | null = null

const state = ref<MatchState | null>(null)
const mySeat = ref<Seat>(0)
const clockSnap = ref<DuelClockPayload | null>(null)

function clockBinding(seat: Seat): {
  remainingMs: number
  running: boolean
  at: number
} | null {
  const snap = clockSnap.value
  if (!snap) return null
  return {
    remainingMs: snap.remainingMs[seat],
    running: snap.runningSeat === seat,
    at: snap.at,
  }
}

const myClock = computed(() => clockBinding(mySeat.value))
const oppClock = computed(() =>
  clockBinding((mySeat.value === 0 ? 1 : 0) as Seat),
)

function applyClock(payload: DuelClockPayload): void {
  clockSnap.value = payload
}
const shownMyLp = ref(8000)
const shownOppLp = ref(8000)
const lpLitMine = ref<'hit' | 'heal' | false>(false)
const lpLitOpp = ref<'hit' | 'heal' | false>(false)
type CompactLpFloat = { id: number; text: string; hit: boolean }
const lpFloatMine = ref<CompactLpFloat | null>(null)
const lpFloatOpp = ref<CompactLpFloat | null>(null)
let lpFloatSeq = 0
let lpFloatMineTimer: ReturnType<typeof setTimeout> | null = null
let lpFloatOppTimer: ReturnType<typeof setTimeout> | null = null
const lpPunchMine = ref(false)
const lpPunchOpp = ref(false)
const lpPunchMineMs = ref(1100)
const lpPunchOppMs = ref(1100)
let lpPunchMineTimer: ReturnType<typeof setTimeout> | null = null
let lpPunchOppTimer: ReturnType<typeof setTimeout> | null = null
const boardStage = ref<HTMLElement | null>(null)
const boardShaking = ref(false)
const boardShakeMs = ref(0)
const lpAnchored = ref(false)
const lpGutterStyle = ref<Record<string, string>>({})
const boardStageStyle = computed(() => {
  const style: Record<string, string> = { ...lpGutterStyle.value }
  if (boardShakeMs.value) style['--duel-shake-ms'] = `${boardShakeMs.value}ms`
  return style
})

/** Room for the LP plate plus the 12px hang off the playmat. */
function lpPlateClear(): number {
  return duelSnug.value ? 220 : 260
}
const BOARD_WORLD_W = 1024
const BOARD_WORLD_H = 768

/**
 * Pixels to inset the board from the left so the LP plates stay in the
 * stage gutter instead of sliding under the card rail. Grows as the
 * window gets narrower; wide screens stay centered.
 */
function boardNudgePx(stageW: number, stageH: number, leftFrac: number): number {
  if (stageW < 8 || stageH < 8) return 0
  const clear = lpPlateClear()
  const heightCanvas = BOARD_WORLD_W * (stageH / BOARD_WORLD_H)
  const letterbox = Math.max(0, stageW - heightCanvas)
  const natural =
    heightCanvas >= stageW
      ? stageW * leftFrac
      : letterbox / 2 + heightCanvas * leftFrac
  const deficit = clear - natural
  if (deficit <= 0) return 0
  if (heightCanvas < stageW && deficit * 2 <= letterbox + 1) {
    return Math.ceil(deficit * 2)
  }
  const shrink = (clear - stageW * leftFrac) / (1 - leftFrac)
  return Math.max(0, Math.min(stageW * 0.42, Math.ceil(shrink)))
}

function layoutDesktopLp(): void {
  if (duelCompact.value) return
  const stage = boardStage.value
  const canvas = game?.canvas
  const scene = duelScene()
  if (!stage || !canvas || !scene) return
  const anchors = scene.desktopLpAnchors()
  if (
    !(anchors.left > 0.05 && anchors.left < 0.8) ||
    !(anchors.oppTop >= 0 && anchors.oppTop < 0.5) ||
    !(anchors.meBottom > 0.5 && anchors.meBottom <= 1)
  ) {
    return
  }
  const sr = stage.getBoundingClientRect()
  const nudge = boardNudgePx(sr.width, sr.height, anchors.left)
  const nextNudge = `${nudge}px`
  if (lpGutterStyle.value['--board-nudge'] !== nextNudge) {
    lpGutterStyle.value = {
      ...lpGutterStyle.value,
      '--board-nudge': nextNudge,
    }
    return
  }
  const cr = canvas.getBoundingClientRect()
  if (cr.width < 8 || cr.height < 8) return
  const x = cr.left - sr.left + cr.width * anchors.left
  const oppTop = cr.top - sr.top + cr.height * anchors.oppTop
  const meBottom = cr.top - sr.top + cr.height * anchors.meBottom
  lpGutterStyle.value = {
    '--board-nudge': nextNudge,
    '--lp-edge-x': `${Math.round(x)}px`,
    '--lp-opp-top': `${Math.round(oppTop)}px`,
    '--lp-me-bottom': `${Math.round(meBottom)}px`,
  }
  lpAnchored.value = true
}

function scheduleDesktopLp(): void {
  requestAnimationFrame(() => {
    layoutDesktopLp()
    requestAnimationFrame(() => layoutDesktopLp())
  })
}
let boardShakeTimer: ReturnType<typeof setTimeout> | null = null

function punchCompactLp(mine: boolean, durationMs = 1100): void {
  if (duelPrefs.motionLevel !== 'full') return
  const ms = Math.max(700, durationMs)
  if (mine) {
    lpPunchMine.value = false
    lpPunchMineMs.value = ms
    if (lpPunchMineTimer) clearTimeout(lpPunchMineTimer)
    requestAnimationFrame(() => {
      lpPunchMine.value = true
    })
    lpPunchMineTimer = setTimeout(() => {
      lpPunchMine.value = false
    }, ms)
    return
  }
  lpPunchOpp.value = false
  lpPunchOppMs.value = ms
  if (lpPunchOppTimer) clearTimeout(lpPunchOppTimer)
  requestAnimationFrame(() => {
    lpPunchOpp.value = true
  })
  lpPunchOppTimer = setTimeout(() => {
    lpPunchOpp.value = false
  }, ms)
}

function onBoardShake(durationMs: number): void {
  if (duelPrefs.motionLevel !== 'full') return
  const ms = Math.max(180, durationMs)
  boardShaking.value = false
  boardShakeMs.value = ms
  if (boardShakeTimer) clearTimeout(boardShakeTimer)
  requestAnimationFrame(() => {
    boardShaking.value = true
  })
  boardShakeTimer = setTimeout(() => {
    boardShaking.value = false
  }, ms)
}

function onCompactLpTick(tick: CompactLpTick): void {
  if (tick.mine) {
    shownMyLp.value = tick.value
    lpLitMine.value = tick.lit
  } else {
    shownOppLp.value = tick.value
    lpLitOpp.value = tick.lit
  }
  if (tick.delta === undefined || tick.delta === 0) return
  const float: CompactLpFloat = {
    id: ++lpFloatSeq,
    text: `${tick.delta > 0 ? '+' : ''}${tick.delta}`,
    hit: tick.delta < 0,
  }
  if (tick.mine) {
    lpFloatMine.value = float
    if (lpFloatMineTimer) clearTimeout(lpFloatMineTimer)
    lpFloatMineTimer = setTimeout(() => {
      lpFloatMine.value = null
    }, 1400)
    return
  }
  lpFloatOpp.value = float
  if (lpFloatOppTimer) clearTimeout(lpFloatOppTimer)
  lpFloatOppTimer = setTimeout(() => {
    lpFloatOpp.value = null
  }, 1400)
}

// Phase/turn can lead the board (opponent attacks) without opening prompts.
const hudPhase = ref<EnginePhase | null>(null)
const hudTurnPlayer = ref<Seat | null>(null)
const hudTurnCount = ref(1)
const hudAbsTurn = ref(1)
// Phaser is still playing a payload; keep phase clicks off until the board matches.
const playbackLocked = ref(false)
const statusKey = ref<string | null>('duel.connecting')
/** This device lost the seat to another one; keep the notice over the board. */
const sessionYielded = ref(false)
/** The room is gone. Keep the notice over the board, with a way home. */
const roomLost = ref(false)
const overlayLeaveLabel = computed(() => {
  if (sessionYielded.value || roomLost.value) return t('duel.backHome')
  return spectateLeaveLabel.value
})
const statusRaw = ref('')
/** Transport dropped. Debounced so a reconnect that lands immediately stays quiet. */
const linkDown = ref(false)
/** The other human seat dropped. The server still holds the duel for one minute. */
const oppLinkDown = ref(false)
const LINK_DOWN_MS = 800
let linkDownTimer: ReturnType<typeof setTimeout> | null = null

function clearLinkDownTimer(): void {
  if (!linkDownTimer) return
  clearTimeout(linkDownTimer)
  linkDownTimer = null
}

/** True while a live board is still waiting for the server to catch this seat up. */
function holdReconnectBanner(): boolean {
  return (
    Boolean(state.value) &&
    !gameOverVisible.value &&
    !sessionYielded.value &&
    !roomLost.value
  )
}

function noteLinkCaughtUp(): void {
  clearLinkDownTimer()
  linkDown.value = false
}

function onSocketLink(up: boolean): void {
  if (up) {
    clearLinkDownTimer()
    // The socket can open before room:reconnect answers. Keep the banner
    // until the board, the result, or a room error arrives.
    if (!holdReconnectBanner()) linkDown.value = false
    return
  }
  if (linkDown.value || linkDownTimer || sessionYielded.value || roomLost.value) return
  linkDownTimer = setTimeout(() => {
    linkDownTimer = null
    if (sessionYielded.value || roomLost.value) return
    linkDown.value = true
  }, LINK_DOWN_MS)
}
const queueElapsedSec = ref(0)
let queueTick: ReturnType<typeof setInterval> | null = null
const queueSearching = computed(
  () =>
    (statusKey.value === 'duel.searching' ||
      statusKey.value === 'duel.searchingCasual' ||
      statusKey.value === 'duel.searchingEvent') &&
    !statusRaw.value &&
    !linkDown.value,
)
const queueElapsedLabel = computed(() => formatQueueElapsed(queueElapsedSec.value))
const statusMessage = computed(() => {
  if (linkDown.value && !sessionYielded.value && !statusRaw.value) {
    return t('duel.reconnecting')
  }
  if (statusRaw.value) return statusRaw.value
  return statusKey.value ? t(statusKey.value) : ''
})

function formatQueueElapsed(total: number): string {
  const safe = Math.max(0, total)
  const h = Math.floor(safe / 3600)
  const m = Math.floor((safe % 3600) / 60)
  const s = safe % 60
  const ss = String(s).padStart(2, '0')
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${ss}`
  return `${m}:${ss}`
}

function syncQueueElapsed(): void {
  queueElapsedSec.value =
    queueBeganAt > 0 ? Math.floor((Date.now() - queueBeganAt) / 1000) : 0
}

function stopQueueClock(): void {
  if (!queueTick) return
  clearInterval(queueTick)
  queueTick = null
}

function startQueueClock(): void {
  stopQueueClock()
  syncQueueElapsed()
  queueTick = setInterval(syncQueueElapsed, 1000)
}

watch(queueSearching, (on) => {
  if (on) startQueueClock()
  else stopQueueClock()
})

const roomCode = ref<string | null>(null)
const tournament = useTournamentStore()
const roundEndsAt = computed(() => {
  const live = tournament.current
  if (!live || live.status !== 'live' || live.sample) return null
  const code = roomCode.value
  const me = auth.user?.id ?? null
  const match = live.matches.find((row) => {
    if (!row.endsAt || row.status === 'done') return false
    if (code && row.roomCode === code) return true
    return Boolean(me && (row.player0?.userId === me || row.player1?.userId === me))
  })
  return match?.endsAt ?? null
})

const tournamentNow = ref(Date.now())
let tournamentClock: ReturnType<typeof setInterval> | null = null
let tournamentAdvanceTimer: ReturnType<typeof setTimeout> | null = null
const heldTournamentMatch = ref<TournamentMatchView | null>(null)

const tournamentRouteMatchId = computed(() =>
  String(route.query.mode || '') === 'tournament' ? String(route.query.match || '') : '',
)

const tournamentRouteMatch = computed(() => {
  const id = tournamentRouteMatchId.value
  const live = tournament.current
  if (!id || !live || live.sample) return null
  return live.matches.find((row) => row.id === id) ?? null
})

/** Keeps the result on screen after the bracket closes and drops the match. */
const tournamentShownMatch = computed(() => {
  const live = tournamentRouteMatch.value
  if (live) return live
  const held = heldTournamentMatch.value
  if (!held || held.status !== 'done' || held.id !== tournamentRouteMatchId.value) return null
  return held
})

const tournamentOpponentName = computed(() => {
  const match = tournamentShownMatch.value
  const me = auth.user?.id
  if (!match || !me) return ''
  if (match.player0?.userId === me) return match.player1?.username ?? ''
  if (match.player1?.userId === me) return match.player0?.username ?? ''
  return ''
})

const tournamentOverlayTitle = computed(() => {
  if (!tournamentRouteMatchId.value || state.value) return ''
  const match = tournamentShownMatch.value
  const me = auth.user?.id
  if (match?.status === 'done') {
    if (me && match.winnerUserId === me) return t('duel.tournamentAdvance')
    return t('duel.tournamentTimeUp')
  }
  if (match?.status === 'live') return t('duel.joining')
  const name = tournamentOpponentName.value
  return name ? t('duel.tournamentWaitFor', { name }) : ''
})

const tournamentCountdown = computed(() => {
  if (state.value) return ''
  const match = tournamentShownMatch.value
  if (!match || match.status !== 'waiting' || !match.readyEndsAt) return ''
  const ends = Date.parse(match.readyEndsAt)
  if (!Number.isFinite(ends)) return ''
  const total = Math.floor(Math.max(0, ends - tournamentNow.value) / 1000)
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
})

function startTournamentClock(): void {
  if (tournamentClock) return
  tournamentNow.value = Date.now()
  tournamentClock = setInterval(() => {
    tournamentNow.value = Date.now()
  }, 250)
}

function stopTournamentClock(): void {
  if (tournamentClock) clearInterval(tournamentClock)
  tournamentClock = null
  if (tournamentAdvanceTimer) clearTimeout(tournamentAdvanceTimer)
  tournamentAdvanceTimer = null
}

watch(tournamentRouteMatch, (match) => {
  if (match) heldTournamentMatch.value = match
})

watch(
  () => tournament.myActiveMatch?.id ?? '',
  (nextId) => {
    if (!isTournament.value || !nextId) return
    if (String(route.query.match || '') === nextId) return
    if (state.value && !matchFinished) return
    if (tournamentAdvanceTimer) clearTimeout(tournamentAdvanceTimer)
    const fromId = String(route.query.match || '')
    tournamentAdvanceTimer = setTimeout(() => {
      tournamentAdvanceTimer = null
      if (route.name !== 'online-duel') return
      if (String(route.query.mode || '') !== 'tournament') return
      if (String(route.query.match || '') !== fromId) return
      if (state.value && !matchFinished) return
      void openTournamentMatch(nextId, 'advance')
    }, 1200)
  },
  { immediate: true },
)

/** Private rooms only. Ranked and casual queues have an internal code. */
const showRoomCode = computed(() => {
  if (!roomCode.value || sessionYielded.value || roomLost.value) return false
  const mode = String(route.query.mode || '')
  return mode === 'create' || mode === 'join'
})

const hoveredCard = ref<CardViewerData | null>(null)
const hoveredCardFaceDown = ref(false)
const hoveredSleeve = ref('')
const graveyardVisible = ref(false)
const graveyardCards = ref<any[]>([])
const graveyardOwner = ref('player')
/** Last pile this seat told the room about. Null means the viewer is closed. */
let sentGyWhose: Seat | null = null
const extraVisible = ref(false)
const extraCards = ref<any[]>([])
const extraOwner = ref('player')

/** English names in hand and on our field — what Polymerization can use. */
const fusionMaterialNames = computed((): string[] | undefined => {
  const s = state.value
  if (!s || isSpectate.value) return undefined
  const z = s.zones[mySeat.value]
  const names: string[] = []
  const push = (cardId: string) => {
    if (!cardId) return
    const name = getCatalogCard(cardId)?.name
    if (name) names.push(name)
  }
  for (const card of z.hand) push(card.cardId)
  for (const card of z.monsters) {
    if (card) push(card.cardId)
  }
  return names
})
const banishedVisible = ref(false)
const banishedCards = ref<any[]>([])
const banishedOwner = ref('player')
const gameOverVisible = ref(false)
const gameOverWinner = ref<'player' | 'opponent' | null>(null)
const gameOverHeadline = ref('')
const gameOverDetail = ref('')
const gameOverReason = ref<string | null>(null)
const gameOverReward = ref(0)
const gameOverNudge = ref(0)
const gameOverOfferCoins = ref(0)
const gameOverRanked = ref<RankedDelta | null>(null)
const gameOverProgress = ref<DuelXpResult | null>(null)
const exodiaVisible = ref(false)
const surrenderAsk = ref(false)
const banlistDialogOpen = ref(false)
const banlistViolations = ref<BanlistViolation[]>([])
const leaveAsk = ref(false)
const positionVisible = ref(false)
const positionCard = ref<{
  id: string
  name: string
  atk?: number
  def?: number
  foil?: string
} | null>(null)
let positionDone: ((pos: CardPosition | null) => void) | null = null

const onPositionSelected = (pos: 'ATK' | 'DEF' | 'SET') => {
  positionVisible.value = false
  positionCard.value = null
  const done = positionDone
  positionDone = null
  done?.(pos === 'DEF' ? CardPosition.DEFENSE : CardPosition.ATTACK)
}

const onPositionCancel = () => {
  positionVisible.value = false
  positionCard.value = null
  const done = positionDone
  positionDone = null
  done?.(null)
}

// Spell/trap activation flash, rendered as an HTML overlay so it draws above the
// phase-manager HUD (a Phaser canvas animation would sit behind it).
const activationCardId = ref<string | null>(null)
const activationFoil = ref<string>('common')
const activationKind = ref<'spell' | 'trap' | 'summon'>('spell')
const activationTurnsMax = ref(0)
const activationTurnShown = ref(0)
const vfxCanvas = ref<HTMLCanvasElement | null>(null)
const sfxMuted = ref(DuelSfx.isMuted())
const toggleSfx = () => {
  DuelSfx.prime()
  sfxMuted.value = DuelSfx.toggle()
}
const animSpeed = ref(AnimationSpeed.getSpeed())
const cycleAnimSpeed = () => {
  animSpeed.value = AnimationSpeed.cycle()
}
const primeSfx = () => DuelSfx.prime()

function nameOf(cardId: string | number | null | undefined): string {
  if (cardId === null || cardId === undefined || cardId === '') return ''
  return localizedName(cardId)
}

function spellTrapFaceUp(s: MatchState, uid: string): boolean {
  for (const z of s.zones) {
    for (const c of z.spellTraps) {
      if (c?.uid === uid) return c.faceUp
    }
  }
  return false
}

/** Face-up Ultimate Offering ignition. The first flip is still a normal Trap. */
function isFaceUpOffering(s: MatchState, uid: string): boolean {
  return findCardId(s, uid) === '80604091' && spellTrapFaceUp(s, uid)
}

function offeringOnly(s: MatchState, uids: string[]): boolean {
  return uids.length > 0 && uids.every((uid) => isFaceUpOffering(s, uid))
}

/** Face-up Ultimate Offering this seat can pay 500 LP for right now. */
function payableOfferingUid(s: MatchState, seat: Seat): string | null {
  if (s.zones[seat].lifePoints < 500) return null
  if (s.activatableUids) {
    return s.activatableUids.find((uid) => isFaceUpOffering(s, uid)) ?? null
  }
  for (const c of s.zones[seat].spellTraps) {
    if (c?.faceUp && String(c.cardId) === '80604091') return c.uid
  }
  return null
}

function findCardId(s: MatchState, uid: string): string | null {
  for (const z of s.zones) {
    const piles = [
      ...z.monsters,
      ...z.spellTraps,
      z.fieldSpell,
      ...z.hand,
      ...z.graveyard,
      ...z.extraDeck,
      ...z.banished,
    ]
    for (const c of piles) {
      if (c && c.uid === uid && c.cardId) return String(c.cardId)
    }
  }
  return null
}

/**
 * Label for the confirm button when the open window has exactly one card on
 * the field. A face-down card, including an opponent's Set, stays unnamed.
 */
function soleFieldTargetLabel(s: MatchState, seat: Seat): string | null {
  const pending = s.pendingTargets
  const flip = s.pendingFlipEffect
  const uids =
    pending?.chooser === seat && pending.min === 1 && pending.max === 1
      ? pending.candidateUids
      : !pending && flip?.chooser === seat
        ? flip.targetUids
        : null
  if (!uids || uids.length !== 1) return null
  const uid = uids[0]
  if (!uid) return null
  let faceUp = false
  let cardId = ''
  let onField = false
  for (const z of s.zones) {
    for (const card of [...z.monsters, ...z.spellTraps, z.fieldSpell]) {
      if (card?.uid !== uid) continue
      onField = true
      faceUp = card.faceUp
      cardId = card.cardId
    }
  }
  if (!onField) return null
  const named = faceUp && cardId ? nameOf(cardId) : ''
  return named
    ? t('duel.prompt.selectNamed', { name: named })
    : t('duel.prompt.selectHighlighted')
}

const cardIdByUid = new Map<string, string>()
const eventLog = ref<DuelLogLine[]>([])

function rememberCards(s: MatchState): void {
  for (const z of s.zones) {
    const piles = [
      ...z.monsters,
      ...z.spellTraps,
      z.fieldSpell,
      ...z.hand,
      ...z.graveyard,
      ...z.extraDeck,
      ...z.banished,
    ]
    for (const c of piles) {
      if (c?.uid && c.cardId) cardIdByUid.set(c.uid, String(c.cardId))
    }
  }
}

function uidName(uid: string): string {
  const id = cardIdByUid.get(uid) ?? (state.value ? findCardId(state.value, uid) : null)
  return nameOf(id)
}

function logPlayerName(seat: Seat): string {
  const raw = state.value?.seats[seat]?.username?.trim()
  if (raw) return raw
  return seat === mySeat.value ? t('duel.you') : t('duel.opponent')
}

function appendEventLog(events: GameEvent[]): void {
  const extra = duelLogLinesFromEvents(events, {
    t,
    nameOf,
    uidName,
    playerName: logPlayerName,
  })
  if (extra.length === 0) return
  const next = eventLog.value.concat(extra)
  eventLog.value = next.length > 200 ? next.slice(-200) : next
}

function resetEventLog(): void {
  eventLog.value = []
  cardIdByUid.clear()
}

function trapPromptText(
  prompt: string,
  chainName?: string,
  promptCode?: string,
): string {
  if (chainName) return t('duel.prompt.chainTo', { name: chainName })
  switch (promptCode) {
    case 'trapOnSummon':
      return t('duel.prompt.trapOnSummon')
    case 'trapOnAttack':
      return t('duel.prompt.trapOnAttack')
    case 'trapOnDamage':
      return t('duel.prompt.trapOnDamage')
    case 'trapActivate':
      return t('duel.prompt.trapActivate')
    default:
      break
  }
  if (prompt.includes('summon')) return t('duel.prompt.trapOnSummon')
  if (prompt.includes('damage')) return t('duel.prompt.trapOnDamage')
  if (prompt.includes('attack')) return t('duel.prompt.trapOnAttack')
  return t('duel.prompt.trapActivate')
}

const toastMessage = ref('')
/** 0–1 down the Phaser canvas: top edge of our hand. */
const handWaitRatio = ref<number | null>(null)
const toastTone = ref<DuelToastTone>('guide')
const selectingTarget = ref(false)
const pickSide = ref<DuelPickSide | null>(null)
const fieldMenu = ref<FieldMenuPayload | null>(null)
const cardPicker = ref<CardPickerRequest | null>(null)
const pickerItems = computed((): CardPickerItem[] =>
  (cardPicker.value?.items ?? []).map((item) => ({
    ...item,
    name: nameOf(item.cardId),
  })),
)
const pickerSource = computed(() => {
  const src = cardPicker.value?.source
  if (!src?.cardId) return undefined
  return {
    cardId: src.cardId,
    foil: src.foil,
    name: localizedName(src.cardId),
  }
})
const menuAnchor = computed(() =>
  fieldMenu.value
    ? {
        x: fieldMenu.value.x,
        y: fieldMenu.value.y,
        below: fieldMenu.value.below,
        side: fieldMenu.value.side,
      }
    : null,
)
// Keep the Phaser canvas deaf while the field menu is open and for a short
// beat after it closes, so Activar cannot click-through onto the monster.
const fieldMenuHold = ref(false)
let fieldMenuHoldTimer: ReturnType<typeof setTimeout> | null = null
watch(fieldMenu, (menu) => {
  if (menu) {
    if (fieldMenuHoldTimer) {
      clearTimeout(fieldMenuHoldTimer)
      fieldMenuHoldTimer = null
    }
    fieldMenuHold.value = true
    return
  }
  fieldMenuHold.value = true
  fieldMenuHoldTimer = setTimeout(() => {
    fieldMenuHold.value = false
    fieldMenuHoldTimer = null
  }, 300)
})
// Change of Heart opens its target window after Activar. The 300 ms canvas
// mute must not still be covering the highlighted monster.
watch(
  () => {
    const pending = state.value?.pendingTargets
    if (!pending || pending.chooser !== mySeat.value) return ''
    return pending.requestId
  },
  (id) => {
    if (!id || fieldMenu.value) return
    if (fieldMenuHoldTimer) {
      clearTimeout(fieldMenuHoldTimer)
      fieldMenuHoldTimer = null
    }
    fieldMenuHold.value = false
  },
)
let toastTimer: ReturnType<typeof setTimeout> | null = null
function showToast(message: string, tone: DuelToastTone = 'guide'): void {
  toastMessage.value = message
  toastTone.value = message ? tone : 'guide'
  if (toastTimer) clearTimeout(toastTimer)
  toastTimer = null
  if (!message) return
  if (tone === 'guide') return
  toastTimer = setTimeout(() => {
    if (toastMessage.value === message) toastMessage.value = ''
  }, 2500)
}

function fieldSideOfUid(
  s: MatchState,
  uid: string,
  seat: Seat,
): 'mine' | 'opp' | null {
  for (const side of [0, 1] as Seat[]) {
    const z = s.zones[side]
    if (
      z.monsters.some((c) => c?.uid === uid) ||
      z.spellTraps.some((c) => c?.uid === uid) ||
      z.fieldSpell?.uid === uid
    ) {
      return side === seat ? 'mine' : 'opp'
    }
  }
  return null
}

function pickSideFromUids(
  s: MatchState,
  uids: string[],
  seat: Seat,
): DuelPickSide | null {
  let mine = false
  let opp = false
  for (const uid of uids) {
    const side = fieldSideOfUid(s, uid, seat)
    if (side === 'mine') mine = true
    else if (side === 'opp') opp = true
  }
  if (mine && opp) return 'both'
  if (mine) return 'mine'
  if (opp) return 'opp'
  return null
}

const promptOpen = computed(() => {
  const s = state.value
  return (
    !!fieldMenu.value ||
    !!cardPicker.value ||
    !!(
      s?.pendingChainOrder ||
      s?.pendingPriority ||
      s?.pendingDiscard ||
      s?.pendingFlipEffect ||
      s?.pendingTargets ||
      s?.pendingCoinCall ||
      s?.pendingChoice ||
      s?.pendingOfferingSummon != null
    )
  )
})

/**
 * Park prompts on the opposite edge of the pick row: opp field → bottom,
 * own field/hand → top, both halves → top, button-only → bottom.
 * Phone zoom on the opponent fills the screen with their field, and the
 * bottom of that view is the monster row. A question there covers the summon.
 */
const promptPlace = computed((): 'top' | 'bottom' | 'mid' => {
  const s = state.value
  // Coin and yes/no have no pick row. On a phone the bottom card sat under
  // the hand, so the only visible line was the engine toast and the Battle
  // Phase looked frozen. Keep the buttons on the board, under the midline.
  if (
    s?.pendingCoinCall?.chooser === mySeat.value ||
    s?.pendingChoice?.chooser === mySeat.value
  ) {
    return 'mid'
  }
  // Ultimate Offering asks you to read your hand. Keep that prompt on the
  // opponent's edge so the hand stays visible.
  if (s?.pendingOfferingSummon === mySeat.value) return 'top'
  if (
    s?.pendingPriority?.responder === mySeat.value &&
    s.pendingPriority.activatableUids.some((uid) => findCardId(s, uid) === '80604091')
  ) {
    return 'top'
  }
  if (
    duelCompact.value &&
    duelPrefs.focusBands &&
    focusBand.value === 'opp'
  ) {
    return 'top'
  }
  let side = pickSide.value
  if (!side && s) {
    if (s.pendingDiscard?.seat === mySeat.value) {
      side = 'mine'
    } else {
      const uids =
        s.pendingFlipEffect?.targetUids ?? s.pendingTargets?.candidateUids
      if (uids?.length) side = pickSideFromUids(s, uids, mySeat.value)
    }
  }
  if (side === 'mine' || side === 'both') return 'top'
  return 'bottom'
})

function watchActor(s: MatchState): Seat | null {
  if (s.pendingChainOrder) return s.pendingChainOrder.chooser
  if (s.pendingPriority) return s.pendingPriority.responder
  if (s.pendingTargets) return s.pendingTargets.chooser
  if (s.pendingFlipEffect) return s.pendingFlipEffect.chooser
  if (s.pendingDiscard) return s.pendingDiscard.seat
  if (s.pendingCoinCall) return s.pendingCoinCall.chooser
  if (s.pendingChoice) return s.pendingChoice.chooser
  if (s.pendingOfferingSummon != null) return s.pendingOfferingSummon
  return null
}

function watchOverDetail(reason: string | null | undefined): string {
  if (reason === 'life-points') return t('duel.watchOver.lifePoints')
  if (reason === 'deck-out') return t('duel.watchOver.deckOut')
  if (reason === 'exodia') return t('duel.watchOver.exodia')
  if (reason === 'surrender') return t('duel.watchOver.surrender')
  if (reason === 'forfeit') return t('duel.watchOver.forfeit')
  if (reason === 'timeout') return t('duel.watchOver.timeout')
  if (reason === 'round-time') return t('duel.watchOver.roundTime')
  return ''
}

function targetPrompt(pending: {
  cardId: string
  addToHandFromDeck?: boolean
}): string {
  if (pending.addToHandFromDeck) return deckSearchPrompt(pending.cardId)
  const name = nameOf(pending.cardId) || t('duel.toast.selectTarget')
  return t('duel.prompt.flipTarget', { name })
}

const promptMessage = computed(() => {
  const s = state.value
  // The coin call (and Blast Juggler / Mushroom Man #2) has to win over an
  // attack-target pick and a field menu. Those used to return here and leave
  // only the toast, while the engine refused every other action.
  if (!isSpectate.value && !isReplay.value && s?.pendingCoinCall?.chooser === mySeat.value) {
    return t('duel.prompt.coinCall', { name: nameOf(s.pendingCoinCall.cardId) })
  }
  if (!isSpectate.value && !isReplay.value && s?.pendingChoice?.chooser === mySeat.value) {
    const name = nameOf(s.pendingChoice.cardId)
    return s.pendingChoice.kind === 'blastJuggler'
      ? t('duel.prompt.blastJuggler', { name })
      : t('duel.prompt.mushroomGive', { name })
  }
  if (fieldMenu.value) return ''
  if (isReplay.value) return ''
  if (isSpectate.value) {
    if (!s) return ''
    const seat = watchActor(s)
    if (seat === null) return ''
    const name = s.seats[seat]?.username || t('duel.opponent')
    return t('duel.watchWaiting', { name })
  }
  if (!s) return ''
  // The list already says what to pick. A second line on the board repeated
  // "choose a target" over that title.
  if (cardPicker.value) return ''
  if (s.pendingChainOrder) {
    if (s.pendingChainOrder.chooser !== mySeat.value) {
      return t('duel.prompt.waitingChainOrder')
    }
    const names = s.pendingChainOrder.cards
      .map((card) => nameOf(card.cardId))
      .filter((name) => name.length > 0)
      .join(', ')
    return t('duel.prompt.chainOrder', { names })
  }
  // Field targeting used to hide this line. The only hint was a short toast,
  // so a boost with a legal monster looked like the card had no target and
  // the phase button did nothing (the engine is waiting for the click).
  if (selectingTarget.value) {
    if (s.pendingTargets?.chooser === mySeat.value) {
      return targetPrompt(s.pendingTargets)
    }
    if (s.pendingFlipEffect?.chooser === mySeat.value) {
      const name = nameOf(s.pendingFlipEffect.cardId) || t('duel.menu.flipSummon')
      return t('duel.prompt.flipTarget', { name })
    }
    return ''
  }
  if (s.pendingDiscard) {
    return s.pendingDiscard.seat === mySeat.value
      ? t('duel.prompt.discard', { n: s.pendingDiscard.count })
      : t('duel.prompt.waitingDiscard')
  }
  if (s.pendingFlipEffect) {
    if (s.pendingFlipEffect.chooser === mySeat.value) {
      const name = nameOf(s.pendingFlipEffect.cardId) || t('duel.menu.flipSummon')
      return t('duel.prompt.flipTarget', { name })
    }
    return t('duel.prompt.waitingTarget')
  }
  if (s.pendingTargets) {
    if (s.pendingTargets.chooser === mySeat.value) {
      return targetPrompt(s.pendingTargets)
    }
    return t('duel.prompt.waitingTarget')
  }
  if (s.pendingCoinCall) {
    if (s.pendingCoinCall.chooser !== mySeat.value) return t('duel.prompt.waitingCoin')
    return t('duel.prompt.coinCall', { name: nameOf(s.pendingCoinCall.cardId) })
  }
  if (s.pendingChoice) {
    if (s.pendingChoice.chooser !== mySeat.value) return t('duel.prompt.waitingChoice')
    const name = nameOf(s.pendingChoice.cardId)
    return s.pendingChoice.kind === 'blastJuggler'
      ? t('duel.prompt.blastJuggler', { name })
      : t('duel.prompt.mushroomGive', { name })
  }
  if (s.pendingOfferingSummon === mySeat.value) {
    return s.zones[mySeat.value].extraNormalSummons > 0
      ? t('duel.prompt.offeringSummon')
      : t('duel.prompt.offeringPay')
  }
  if (
    s.pendingOfferingSummon !== null &&
    s.pendingOfferingSummon !== mySeat.value
  ) {
    return t('duel.prompt.waitingOffering')
  }
  if (s.pendingPriority) {
    if (s.pendingPriority.responder !== mySeat.value) {
      return t('duel.prompt.waitingRespond')
    }
    const link = s.pendingPriority.chainLink
      ? (s.chain ?? []).find((l) => l.link === s.pendingPriority!.chainLink)
      : undefined
    if (offeringOnly(s, s.pendingPriority.activatableUids)) {
      return t('duel.prompt.offeringIgnite')
    }
    const chainName = link ? nameOf(link.cardId) || t('duel.trap') : undefined
    return trapPromptText(
      s.pendingPriority.prompt,
      chainName,
      s.pendingPriority.promptCode,
    )
  }
  return ''
})

const seatWait = computed((): 'me' | 'opp' | null => {
  if (fieldMenu.value || !promptMessage.value) return null
  const s = state.value
  if (!s) return null
  let seat: Seat | null = null
  if (isSpectate.value) {
    seat = watchActor(s)
  } else if (s.pendingDiscard && s.pendingDiscard.seat !== mySeat.value) {
    seat = s.pendingDiscard.seat
  } else if (s.pendingFlipEffect && s.pendingFlipEffect.chooser !== mySeat.value) {
    seat = s.pendingFlipEffect.chooser
  } else if (s.pendingTargets && s.pendingTargets.chooser !== mySeat.value) {
    seat = s.pendingTargets.chooser
  } else if (s.pendingCoinCall && s.pendingCoinCall.chooser !== mySeat.value) {
    seat = s.pendingCoinCall.chooser
  } else if (s.pendingChoice && s.pendingChoice.chooser !== mySeat.value) {
    seat = s.pendingChoice.chooser
  } else if (
    s.pendingOfferingSummon != null &&
    s.pendingOfferingSummon !== mySeat.value
  ) {
    seat = s.pendingOfferingSummon
  } else if (s.pendingChainOrder && s.pendingChainOrder.chooser !== mySeat.value) {
    seat = s.pendingChainOrder.chooser
  } else if (s.pendingPriority && s.pendingPriority.responder !== mySeat.value) {
    seat = s.pendingPriority.responder
  }
  if (seat === null) return null
  return seat === mySeat.value ? 'me' : 'opp'
})

/** Whose graveyard each seat has open. Null when that viewer is closed. */
const gyOpen = ref<[Seat | null, Seat | null]>([null, null])

function glanceLine(viewer: Seat): string {
  const whose = gyOpen.value[viewer]
  const s = state.value
  if (whose === null || !s || gameOverVisible.value) return ''
  if (!isSpectate.value && viewer === mySeat.value) return ''
  const own = whose === viewer
  if (isSpectate.value) {
    const name = s.seats[viewer]?.username || t('duel.opponent')
    if (own) return t('duel.prompt.viewingOwnGyNamed', { name })
    const other = s.seats[whose]?.username || t('duel.opponent')
    return t('duel.prompt.viewingOppGyNamed', { name, other })
  }
  return own ? t('duel.prompt.viewingOwnGy') : t('duel.prompt.viewingYourGy')
}

/** Waiting line wins. Otherwise say which graveyard that seat has open. */
function railNote(side: 'me' | 'opp'): string {
  if (seatWait.value === side) return promptMessage.value
  const viewer = (side === 'me' ? mySeat.value : mySeat.value === 0 ? 1 : 0) as Seat
  return glanceLine(viewer)
}

/**
 * Passive "waiting" lines sit under the duelist who still has to answer.
 * Your own prompts (buttons, discard, targets) stay on the board overlay.
 * A graveyard glance uses the same spot when nobody is waiting on that seat.
 */
const handWaitTop = computed(() => {
  const ratio = handWaitRatio.value
  if (ratio == null || !railNote('opp')) return null
  const canvas = document.querySelector(
    '#online-game-container canvas',
  ) as HTMLCanvasElement | null
  const host = document.getElementById('online-game-container')
  if (!canvas || !host) return `${ratio * 100}%`
  const card = canvas.getBoundingClientRect()
  const box = host.getBoundingClientRect()
  const y = card.top - box.top + card.height * ratio
  return `${Math.round(y)}px`
})

/** Engine id of the trap question this seat must answer. Empty for menus. */
const trapQuestionId = computed(() => {
  if (fieldMenu.value) return ''
  const pending = state.value?.pendingPriority
  if (!pending || pending.responder !== mySeat.value) return ''
  return pending.requestId
})

const promptActions = computed((): DuelPromptAction[] => {
  if (isSpectate.value || isReplay.value) return []
  const owned = state.value
  if (owned?.pendingCoinCall?.chooser === mySeat.value) {
    return [
      { id: 'coin:heads', label: t('duel.prompt.heads'), kind: 'primary' },
      { id: 'coin:tails', label: t('duel.prompt.tails'), kind: 'primary' },
    ]
  }
  if (owned?.pendingChoice?.chooser === mySeat.value) {
    return [
      { id: 'choice:yes', label: t('duel.prompt.choiceYes'), kind: 'primary' },
      { id: 'choice:no', label: t('duel.prompt.choiceNo'), kind: 'pass' },
    ]
  }
  if (fieldMenu.value) {
    const actions: DuelPromptAction[] = fieldMenu.value.labels.map((label, i) => ({
      id: `menu:${i}`,
      label,
      kind: 'primary',
    }))
    actions.push({ id: 'menu:cancel', label: t('duel.prompt.cancel'), kind: 'pass' })
    return actions
  }
  const s = state.value
  if (!s) return []
  if (s.pendingChainOrder?.chooser === mySeat.value && !cardPicker.value) {
    return [{ id: 'chain-order', label: t('duel.prompt.chainOrderGo'), kind: 'primary' }]
  }
  // A Tribute Summon pick is not an engine window. Attack targeting sits
  // on the opponent's row, so its Cancel stays in the card rail.
  if (selectingTarget.value) {
    if (s.pendingTargets?.chooser === mySeat.value) return []
    if (s.pendingFlipEffect?.chooser === mySeat.value) return []
    if (pickSide.value === 'opp') return []
    return [{ id: 'board:cancel', label: t('duel.prompt.cancel'), kind: 'pass' }]
  }
  if (s.pendingCoinCall) {
    if (s.pendingCoinCall.chooser !== mySeat.value) return []
    return [
      { id: 'coin:heads', label: t('duel.prompt.heads'), kind: 'primary' },
      { id: 'coin:tails', label: t('duel.prompt.tails'), kind: 'primary' },
    ]
  }
  if (s.pendingChoice) {
    if (s.pendingChoice.chooser !== mySeat.value) return []
    return [
      { id: 'choice:yes', label: t('duel.prompt.choiceYes'), kind: 'primary' },
      { id: 'choice:no', label: t('duel.prompt.choiceNo'), kind: 'pass' },
    ]
  }
  if (s.pendingOfferingSummon === mySeat.value) {
    const actions: DuelPromptAction[] = []
    if (s.zones[mySeat.value].extraNormalSummons <= 0) {
      const uid = payableOfferingUid(s, mySeat.value)
      if (uid) {
        actions.push({
          id: `offering-pay:${uid}`,
          label: t('duel.prompt.offeringPayAction'),
          kind: 'primary',
        })
      }
      actions.push({ id: 'offering-pass', label: t('duel.prompt.pass'), kind: 'pass' })
    }
    return actions
  }
  if (!cardPicker.value) {
    const label = soleFieldTargetLabel(s, mySeat.value)
    if (label) {
      return [{ id: 'sole-target', label, kind: 'primary' }]
    }
  }
  const pending = s.pendingPriority
  if (!pending || pending.responder !== mySeat.value) return []
  const onlyOffering = offeringOnly(s, pending.activatableUids)
  const actions: DuelPromptAction[] = pending.activatableUids.map((uid) => {
    const id = findCardId(s, uid)
    const name = (id && nameOf(id)) || t('duel.trap')
    const label = isFaceUpOffering(s, uid)
      ? onlyOffering
        ? t('duel.prompt.offeringPayAction')
        : t('duel.prompt.offeringPayBtn', { name })
      : t('duel.prompt.activate', { name })
    return { id: uid, label, kind: 'primary' }
  })
  actions.push({ id: 'pass', label: t('duel.prompt.pass'), kind: 'pass' })
  return actions
})

/** Pay 500 LP and No, pass share one row so the card uses the board width. */
const promptActionsSplit = computed(() => {
  if (menuAnchor.value) return false
  const actions = promptActions.value
  if (actions.length !== 2) return false
  if (actions.every((action) => action.id.startsWith('coin:') || action.id.startsWith('choice:'))) {
    return true
  }
  const primary = actions.find((action) => action.kind === 'primary')
  const pass = actions.find((action) => action.kind === 'pass')
  if (!primary || !pass) return false
  if (primary.id.startsWith('offering-pay:')) return true
  const s = state.value
  return !!s && isFaceUpOffering(s, primary.id)
})

/**
 * Phase clicks wait while a real prompt is on screen. A trap window whose
 * question failed to draw stays clickable, and that click passes the window.
 */
const phaseClicksLocked = computed(
  () =>
    playbackLocked.value ||
    !!fieldMenu.value ||
    (!!cardPicker.value && !cardPicker.value.viewOnly) ||
    positionVisible.value ||
    fieldMenuHold.value ||
    promptMessage.value !== '' ||
    promptActions.value.length > 0,
)

const duelScene = (): NetworkDuelScene | undefined =>
  game?.scene.getScene('NetworkDuelScene') as NetworkDuelScene | undefined

const shuffleCooling = ref(false)
let shuffleCoolTimer: ReturnType<typeof setTimeout> | null = null

const myHandCount = computed(
  () => state.value?.zones[mySeat.value]?.hand.length ?? 0,
)

const canShuffleHand = computed(
  () =>
    !!state.value &&
    !introLocked.value &&
    !playbackLocked.value &&
    !promptOpen.value &&
    myHandCount.value >= 2 &&
    !shuffleCooling.value,
)

function shuffleHand(): void {
  if (!canShuffleHand.value) return
  shuffleCooling.value = true
  if (shuffleCoolTimer) clearTimeout(shuffleCoolTimer)
  shuffleCoolTimer = setTimeout(() => {
    shuffleCooling.value = false
    shuffleCoolTimer = null
  }, 600)
  duelScene()?.requestHandShuffle()
}

const onPickerHover = (item: CardPickerItem): void => {
  hoveredCard.value = {
    id: item.cardId,
    name: item.name ?? nameOf(item.cardId),
    type: '',
    foil: item.foil,
  }
  hoveredCardFaceDown.value = false
}

const onPickerPick = (uid: string): void => {
  duelScene()?.resolvePicker([uid])
}

const onPickerConfirm = (uids: string[]): void => {
  duelScene()?.resolvePicker(uids)
}

const onPickerCancel = (): void => {
  duelScene()?.resolvePicker(null)
}

const onPromptAction = (id: string): void => {
  const scene = duelScene()
  if (id === 'board:cancel') {
    scene?.cancelBoardSelection()
    return
  }
  if (id.startsWith('menu:')) {
    scene?.pickMenuAction(id === 'menu:cancel' ? -1 : Number(id.slice(5)))
    return
  }
  if (id === 'offering-pass') {
    scene?.passOfferingSummon()
    return
  }
  if (id.startsWith('offering-pay:')) {
    scene?.payOffering(id.slice('offering-pay:'.length))
    return
  }
  if (id === 'sole-target') {
    scene?.confirmSoleTarget()
    return
  }
  if (id === 'chain-order') {
    scene?.confirmChainOrder()
    return
  }
  if (id === 'coin:heads' || id === 'coin:tails') {
    scene?.callCoin(id === 'coin:heads' ? 'heads' : 'tails')
    return
  }
  if (id === 'choice:yes' || id === 'choice:no') {
    scene?.answerChoice(id === 'choice:yes')
    return
  }
  scene?.respondToPriority(id === 'pass' ? null : id)
}

/**
 * Enter activates the prompt; Esc chooses No, pass (or cancels a card menu).
 * A field menu with several options stays on the pointer — Enter only confirms
 * it when Activar is the sole choice. Several traps: Enter takes the first.
 */
function promptHotkeyId(key: string): string | null {
  const actions = promptActions.value
  if (actions.length === 0) return null
  if (key === 'Escape') {
    return actions.find((action) => action.kind === 'pass')?.id ?? null
  }
  if (key !== 'Enter') return null
  const primaries = actions.filter((action) => action.kind === 'primary')
  if (primaries.length === 0) return null
  const menu = primaries.every((action) => action.id.startsWith('menu:'))
  if (menu && primaries.length !== 1) return null
  return primaries[0]?.id ?? null
}

function promptHotkeyBlocked(event: KeyboardEvent): boolean {
  if (event.repeat || event.metaKey || event.ctrlKey || event.altKey || event.isComposing) {
    return true
  }
  const target = event.target
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  // A focused rail button (settings, chat send) keeps Enter for itself.
  if (event.key === 'Enter' && (tag === 'BUTTON' || tag === 'A')) {
    return !target.closest('.duel-prompt-overlay')
  }
  return false
}

function onPromptHotkey(event: KeyboardEvent): void {
  if (promptHotkeyBlocked(event)) return
  if (boardModalOpen.value) return
  const id = promptHotkeyId(event.key)
  if (!id) return
  event.preventDefault()
  event.stopPropagation()
  onPromptAction(id)
}

function applyPhaseHud(s: MatchState): void {
  hudPhase.value = s.phase
  hudTurnPlayer.value = s.turnPlayer
  hudTurnCount.value = s.turnCount
  hudAbsTurn.value = s.absoluteTurnCount
}

/** Prompts follow the drawn Phaser board, not the raw engine snapshot. */
function applyBoardState(s: MatchState): void {
  stampSeatNames(s)
  state.value = s
  rememberCards(s)
  applyPhaseHud(s)
  if (!playbackLocked.value) {
    shownMyLp.value = s.zones[mySeat.value]?.lifePoints ?? 8000
    const opp = (mySeat.value === 0 ? 1 : 0) as Seat
    shownOppLp.value = s.zones[opp]?.lifePoints ?? 8000
  }
  stallWatch.noteProgress()
  reportLocalDuel(s)
  checkCardFault()
}

function reportLocalDuel(s: MatchState): void {
  if (!isLocalDuel.value) return
  if (matchFinished || s.status !== 'active') {
    clearLiveAiDuel()
    return
  }
  noteLiveAiDuel({
    turn: s.absoluteTurnCount,
    phase: s.phase,
    turnSeat: s.turnPlayer,
    difficulty: parseAiDeckDifficulty(duelPrefs.vsAiDifficulty),
    lifePoints: [s.zones[0].lifePoints, s.zones[1].lifePoints],
    hand: [s.zones[0].hand.length, s.zones[1].hand.length],
    deck: [s.zones[0].deck.length, s.zones[1].deck.length],
    cpuName: s.seats[1]?.username || 'CPU',
    begunAt: matchBeganAt || Date.now(),
  })
}

let aiKillReason: LiveAiKill | null = null
let aiCapTimer: ReturnType<typeof setTimeout> | null = null

function isBrowserAiDuel(): boolean {
  return String(route.query.mode || 'matchmaking') === 'local'
}

function clearAiCap(): void {
  if (!aiCapTimer) return
  clearTimeout(aiCapTimer)
  aiCapTimer = null
}

function endLocalAi(reason: LiveAiKill): void {
  if (!isBrowserAiDuel() || matchFinished || aiKillReason || !client) return
  if (state.value && state.value.status !== 'active') return
  aiKillReason = reason
  clearAiCap()
  gameOverDetail.value = t(
    reason === 'time' ? 'duel.overBy.aiTooLong' : 'duel.overBy.aiEnded',
  )
  client.send({ type: ActionType.SURRENDER })
}

function armAiCap(): void {
  clearAiCap()
  if (!isBrowserAiDuel() || !matchBeganAt || matchFinished) return
  const left = AI_DUEL_MAX_MS - (Date.now() - matchBeganAt)
  if (left <= 0) {
    endLocalAi('time')
    return
  }
  aiCapTimer = setTimeout(() => endLocalAi('time'), left)
}

function onAiCapVisible(): void {
  if (document.visibilityState === 'hidden') {
    noteDuelHidden()
    return
  }
  if (!isBrowserAiDuel() || !matchBeganAt || matchFinished || aiKillReason) return
  if (Date.now() - matchBeganAt >= AI_DUEL_MAX_MS) endLocalAi('time')
}

let activationTimer: ReturnType<typeof setTimeout> | null = null
let activationCountTimer: ReturnType<typeof setInterval> | null = null
const ACTIVATION_OVERLAY_MS = 1200

function showActivation(
  cardId: string,
  kind: 'spell' | 'trap' | 'summon' = 'spell',
  foil?: string,
  turnMax = 0,
): void {
  if (!cardId) return
  activationCardId.value = cardId
  activationFoil.value = foil ?? 'common'
  activationKind.value = kind
  activationTurnsMax.value = Math.max(0, turnMax)
  activationTurnShown.value = turnMax > 0 ? 1 : 0
  if (activationTimer) clearTimeout(activationTimer)
  if (activationCountTimer) {
    clearInterval(activationCountTimer)
    activationCountTimer = null
  }
  if (turnMax > 1) {
    const step = Math.floor(ACTIVATION_OVERLAY_MS / turnMax)
    activationCountTimer = setInterval(() => {
      if (activationTurnShown.value >= turnMax) {
        if (activationCountTimer) {
          clearInterval(activationCountTimer)
          activationCountTimer = null
        }
        return
      }
      activationTurnShown.value += 1
    }, step)
  }
  activationTimer = setTimeout(() => {
    activationCardId.value = null
    activationTurnsMax.value = 0
    if (activationCountTimer) {
      clearInterval(activationCountTimer)
      activationCountTimer = null
    }
  }, ACTIVATION_OVERLAY_MS)
}

const turnCount = computed(() => hudTurnCount.value || state.value?.turnCount || 1)
const currentPlayer = computed(() => {
  const tp = hudTurnPlayer.value ?? state.value?.turnPlayer
  return state.value && tp === mySeat.value ? Player.PLAYER_1 : Player.PLAYER_2
})
const fieldAmbience = computed(() => activeFieldAmbience(state.value))
const hudRevision = computed(
  () =>
    `${hudPhase.value}-${hudTurnPlayer.value}-${hudAbsTurn.value}-${playbackLocked.value}`,
)
const bannerPhase = computed(() => {
  if (
    diceVisible.value ||
    vsVisible.value ||
    rosterVisible.value ||
    gameOverVisible.value ||
    exodiaVisible.value
  ) {
    return null
  }
  return hudPhase.value
})
const bannerOpponent = computed(
  () => currentPlayer.value === Player.PLAYER_2,
)
const spectateTurnName = computed(() => {
  if (!(isSpectate.value || isReplay.value) || !state.value) return ''
  const seat = hudTurnPlayer.value ?? state.value.turnPlayer
  return state.value.seats[seat]?.username || ''
})
const compactTurnTitle = computed(() => {
  const turn = t('duel.turn', { n: turnCount.value })
  if (spectateTurnName.value) return `${turn} · ${spectateTurnName.value}`
  return `${turn} · ${
    bannerOpponent.value ? t('duel.opponentTurn') : t('duel.yourTurn')
  }`
})
const anyOverlayOpen = computed(
  () =>
    diceVisible.value ||
    vsVisible.value ||
    rosterVisible.value ||
    graveyardVisible.value ||
    extraVisible.value ||
    banishedVisible.value ||
    gameOverVisible.value ||
    exodiaVisible.value ||
    positionVisible.value ||
    !!cardPicker.value ||
    fieldMenuHold.value ||
    linkDown.value ||
    (!!statusMessage.value && !state.value),
)
/** Full-screen lists cover the rail. Graveyard, Extra and banished leave it. */
const pickerCoversRail = computed(
  () => !!cardPicker.value || positionVisible.value,
)
const viewerStowed = computed(
  () => duelCompact.value && duelPrefs.hideCardViewer,
)

const adapter = markRaw(
  new NetworkPhaseAdapter(
    () => state.value,
    () => mySeat.value,
    (a) => {
      if (isSpectate.value || linkDown.value) return
      if (a.type === ActionType.ADVANCE_PHASE) duelScene()?.dropTributePick()
      client?.send(a)
    },
    () =>
      hudPhase.value == null || hudTurnPlayer.value == null
        ? null
        : {
            phase: hudPhase.value,
            turnPlayer: hudTurnPlayer.value,
            turnCount: hudTurnCount.value,
            absoluteTurnCount: hudAbsTurn.value,
          },
    () => playbackLocked.value,
  ),
)

function wouldForfeit(): boolean {
  const local = String(route.query.mode || 'matchmaking') === 'local'
  if (local || isSpectate.value || isReplay.value || !matchStarted || matchFinished) return false
  // A bracket seat that walks out during the dice still loses the match.
  if (isTournament.value) return true
  // Dice, VS, and a dead canvas are not a board yet. Leaving them must
  // not hand the ranked win to the other seat.
  return duelScene()?.boardReady() ?? false
}

const goHome = () => {
  if (sessionYielded.value || roomLost.value) {
    confirmLeave()
    return
  }
  if (wouldForfeit()) {
    surrenderAsk.value = false
    leaveAsk.value = true
    gearOpen.value = false
    return
  }
  confirmLeave()
}

function confirmLeave(): void {
  surrenderAsk.value = false
  leaveAsk.value = false
  const forfeit = wouldForfeit()
  client?.dispose({ forfeit })
  if (isReplay.value) {
    if (window.history.length > 1) router.back()
    else void router.push('/')
    return
  }
  if (!isSpectate.value || spectateFromTournament.value) {
    void router.push('/')
    return
  }
  void router.push(spectateFromOps.value ? adminClientPath() : { name: 'watch' })
}
const playAgain = () => {
  void restartDuelSession()
}

function playRankedOffer(): void {
  void restartDuelSession({
    mode: 'matchmaking',
    username: player.username,
  })
}

const rankedCallPending = ref(false)

async function acceptRankedCall(callId: string): Promise<void> {
  if (rankedCallSkip.value.includes(callId)) return
  if (rankedCallPending.value) return
  rankedCallPending.value = true
  const ok = await requestRankedCall(callId)
  const dismissed = rankedCallSkip.value.includes(callId)
  if (!ok || dismissed) {
    if (ok && dismissed) releaseRankedClaim(callId)
    dropRankedCall(callId)
    rankedCallPending.value = false
    return
  }
  stageRankedCall(callId)
  try {
    await restartDuelSession({
      mode: 'matchmaking',
      username: player.username,
    })
  } finally {
    rankedCallPending.value = false
  }
}
const onOverlayHover = (card: any) => {
  hoveredCard.value = card
  hoveredCardFaceDown.value = false
}
const cancelBoardSelection = () => {
  duelScene()?.cancelBoardSelection()
}

const isLocalDuel = computed(
  () => String(route.query.mode || 'matchmaking') === 'local',
)

function gyWhoseNow(): Seat | null {
  if (!graveyardVisible.value) return null
  if (graveyardOwner.value === 'opponent') {
    return (mySeat.value === 0 ? 1 : 0) as Seat
  }
  return mySeat.value
}

function publishGyGlance(force = false): void {
  if (isSpectate.value || isReplay.value || isLocalDuel.value) return
  const whose = gyWhoseNow()
  if (!force && whose === sentGyWhose) return
  sentGyWhose = whose
  client?.sendGyGlance?.(whose ?? 0, whose !== null)
}

watch([graveyardVisible, graveyardOwner], () => publishGyGlance())
/** Rank medals sit on the mobile LP column and cover the opponent's zones in PvP. */
const showCompactRank = computed(() => isLocalDuel.value)
const vsVisible = ref(false)
const rosterVisible = ref(false)
const rosterPair = ref<[DuelRosterCard, DuelRosterCard] | null>(null)
const diceVisible = ref(false)
const aiPicked = ref(false)
const aiPickVisible = computed(() => isLocalDuel.value && !aiPicked.value)
const dicePreset = ref<DiceOffResult | null>(null)
const localCpuAvatar = ref<AvatarConfig>(pickCpuAvatar(1))
const vsOppAvatar = ref<AvatarConfig>(parseAvatarConfig(undefined))
const vsOppName = ref('')
/** Name this match should keep showing after a rename, until the next match. */
const seatRename = ref<{ previous: string; username: string } | null>(null)

function stampSeatNames(s?: MatchState | null): void {
  const swap = seatRename.value
  const board = s ?? state.value
  if (!swap || !board) return
  for (const seat of board.seats) {
    if (chatNamesMatch(seat.username, swap.previous)) seat.username = swap.username
  }
  if (chatNamesMatch(vsOppName.value, swap.previous)) vsOppName.value = swap.username
}

watch(accountRename, (swap) => {
  if (!swap || isReplay.value) return
  seatRename.value = { previous: swap.previous, username: swap.username }
  stampSeatNames()
})
const introLocked = computed(
  () =>
    vsVisible.value ||
    rosterVisible.value ||
    diceVisible.value ||
    aiPickVisible.value,
)
const rankedCallVisible = computed(
  () =>
    isLocalDuel.value &&
    !!state.value &&
    state.value.status === 'active' &&
    !introLocked.value &&
    !gameOverVisible.value &&
    !exodiaVisible.value,
)
watch(introLocked, (locked) => {
  if (!locked) nextTick(() => scheduleDesktopLp())
})

// Phaser listens for touches on window, so a dialog on top of the canvas still
// hits Extra / Graveyard unless the scene input is muted for the whole modal.
// The compact settings gear is one of those dialogs: its scrim sits on the
// Extra Deck, and a tap on the open gear would open the pile.
const boardModalOpen = computed(
  () =>
    positionVisible.value ||
    !!cardPicker.value ||
    graveyardVisible.value ||
    extraVisible.value ||
    banishedVisible.value ||
    gameOverVisible.value ||
    exodiaVisible.value ||
    surrenderAsk.value ||
    leaveAsk.value ||
    diceVisible.value ||
    vsVisible.value ||
    rosterVisible.value ||
    aiPickVisible.value ||
    (duelCompact.value && gearOpen.value) ||
    linkDown.value,
)
watch(
  boardModalOpen,
  (open) => {
    duelScene()?.setModalInputBlocked(open)
  },
  { flush: 'sync' },
)

function boardPromptVisible(): boolean {
  return (
    !!fieldMenu.value ||
    !!cardPicker.value ||
    positionVisible.value ||
    fieldMenuHold.value ||
    promptMessage.value !== '' ||
    promptActions.value.length > 0
  )
}

function checkBattleStall(): void {
  const s = state.value
  if (!matchStarted || matchFinished || !s || s.status !== 'active') return
  if (introLocked.value || gameOverVisible.value || exodiaVisible.value) return
  if (document.visibilityState === 'hidden') return
  const phase = playbackLocked.value ? (hudPhase.value ?? s.phase) : s.phase
  const hit = detectBattleStall(s, {
    phase,
    mySeat: mySeat.value,
    playbackLocked: playbackLocked.value,
    promptVisible: boardPromptVisible(),
    selecting: selectingTarget.value,
  })
  if (!hit) return
  const quietMs = stallWatch.quietMs()
  if (quietMs < stallQuietLimit(hit.reason)) return
  if (!stallWatch.claim(hit.signature)) return
  const mode = isLocalDuel.value
    ? 'local'
    : String(route.query.mode || 'matchmaking')
  const debug: StallDebug = stallWatch.debug(s, mySeat.value)
  const detail: Record<string, string> = {}
  for (const [key, value] of Object.entries(debug)) {
    if (value) detail[key] = value
  }
  const card = duelPendingCard(s)
  track('duel_stall', {
    mode,
    ...(card ? { card } : {}),
    reason: hit.reason,
    step: hit.step,
    pending: hit.pending,
    turn: hit.turn,
    responder: hit.responder,
    cpu: hit.cpu,
    quietMs,
    turnN: hit.turnN,
    ...detail,
  })
}

function checkCardFault(): void {
  const s = state.value
  if (!matchStarted || matchFinished || !s || s.status !== 'active') return
  if (introLocked.value || gameOverVisible.value || exodiaVisible.value) return
  if (document.visibilityState === 'hidden') return
  const quietMs = stallWatch.quietMs()
  for (const hit of detectCardFaults(s, {
    mySeat: mySeat.value,
    promptVisible: boardPromptVisible(),
    selecting: selectingTarget.value,
  })) {
    if (!hit.immediate && quietMs < STALL_QUIET_MS) continue
    reportCardFault(hit, quietMs)
  }
}

/** Monster tap after a paid Offering still could not open a summon. */
function onOfferingHandFault(): void {
  reportCardFault(offeringHandFault(), stallWatch.quietMs())
}

let hiddenNotes = 0

function duelModeLabel(): string {
  return isLocalDuel.value ? 'local' : String(route.query.mode || 'matchmaking')
}

function noteDuelHidden(): void {
  if (isSpectate.value || isReplay.value) return
  if (!matchStarted || matchFinished || introLocked.value) return
  if (hiddenNotes >= 6) return
  hiddenNotes += 1
  const s = state.value
  track('duel_hidden', {
    room: roomCode.value ?? (isLocalDuel.value ? 'local' : ''),
    mode: duelModeLabel(),
    phase: s?.phase ?? '',
    turn: s && s.turnPlayer === mySeat.value ? 'me' : 'opp',
    turnN: s?.absoluteTurnCount ?? 0,
  })
}

function checkBoardGaps(): void {
  if (isSpectate.value || isReplay.value) return
  if (!matchStarted || matchFinished) return
  if (introLocked.value || gameOverVisible.value || exodiaVisible.value) return
  if (document.visibilityState === 'hidden' || playbackLocked.value) return
  const s = state.value
  const scene = duelScene()
  if (!s || s.status !== 'active' || !scene) return
  const mode = duelModeLabel()
  const room = roomCode.value ?? (isLocalDuel.value ? 'local' : '')
  for (const gap of scene.boardGaps()) {
    track('board_gap', {
      card: gap.card,
      zone: gap.zone,
      face: gap.face,
      side: gap.side,
      phase: s.phase,
      turn: s.turnPlayer === mySeat.value ? 'me' : 'opp',
      turnN: s.absoluteTurnCount,
      mode,
      room,
    })
  }
}

function reportCardFault(hit: CardFaultHit, quietMs: number): void {
  const s = state.value
  if (isSpectate.value) return
  if (!matchStarted || matchFinished || !s || s.status !== 'active') return
  if (introLocked.value || gameOverVisible.value || exodiaVisible.value) return
  if (!faultWatch.claim(hit.signature)) return
  const debug = stallWatch.debug(s, mySeat.value)
  const mode = isLocalDuel.value
    ? 'local'
    : String(route.query.mode || 'matchmaking')
  track('card_fault', {
    card: hit.card,
    reason: hit.reason,
    mode,
    phase: s.phase,
    turn: s.turnPlayer === mySeat.value ? 'me' : 'opp',
    cpu: s.seats[0].isAI || s.seats[1].isAI,
    turnN: s.absoluteTurnCount,
    quietMs,
    ...(debug.prompt ? { prompt: debug.prompt } : {}),
    ...(debug.atk ? { atk: debug.atk } : {}),
    ...(debug.field ? { field: debug.field } : {}),
    ...(debug.recent ? { recent: debug.recent } : {}),
  })
}
const canSurrender = computed(
  () =>
    Boolean(
      state.value &&
        !isSpectate.value &&
        !isReplay.value &&
        !introLocked.value &&
        !gameOverVisible.value &&
        !exodiaVisible.value,
    ),
)

function confirmSurrender(): void {
  surrenderAsk.value = false
  client?.send({ type: ActionType.SURRENDER })
}

async function onMatchOver(over: GameOverPayload): Promise<void> {
  noteLinkCaughtUp()
  matchFinished = true
  clearLiveAiDuel()
  gameOverNudge.value = 0
  gameOverOfferCoins.value = 0
  if (isSpectate.value || isReplay.value) {
    surrenderAsk.value = false
    leaveAsk.value = false
    if (spectateFromTournament.value) {
      confirmLeave()
      return
    }
    const seat = over.winner
    const name =
      seat === 0 || seat === 1 ? state.value?.seats[seat]?.username || '' : ''
    gameOverHeadline.value = name
      ? t('duel.watchWinner', { name })
      : t('duel.watchDraw')
    gameOverDetail.value = watchOverDetail(over.winReason)
    gameOverReward.value = 0
    gameOverRanked.value = null
    gameOverProgress.value = null
    gameOverReason.value = over.winReason ?? null
    gameOverWinner.value = over.winner === 0 ? 'player' : 'opponent'
    gameOverVisible.value = true
    return
  }
  gameOverWinner.value = over.winner === mySeat.value ? 'player' : 'opponent'
  surrenderAsk.value = false
  leaveAsk.value = false
  let delta = 0
  let progress: DuelXpResult | null = over.progress ?? null
  if (isLocalDuel.value) {
    const recorded = client instanceof LocalEngineClient ? client.exportReplay() : null
    const token = auth.token
    if (recorded && token) void postAiReplay(token, recorded).catch(() => {})
    const paid = await wallet.reportAiPayout({
      outcome: over.winner === mySeat.value ? 'win' : 'loss',
      difficulty: parseAiDeckDifficulty(duelPrefs.vsAiDifficulty),
      surrendered: over.winReason === 'surrender',
      metrics: over.metrics,
      durationMs: matchBeganAt ? Date.now() - matchBeganAt : undefined,
      begunAt: matchBeganAt || undefined,
      cardResult:
        over.winner == null
          ? 'draw'
          : over.winner === mySeat.value
            ? 'win'
            : 'loss',
    })
    delta = paid.delta
    progress = paid.progress
    if (paid.rankedOffer) {
      rankedLobby.setOfferPending(paid.rankedOffer.pending)
      if (duelPrefs.rankedPrompts) {
        gameOverOfferCoins.value = paid.rankedOffer.coins
      }
    }
  } else {
    const mine = over.rewards?.find((row) => row.seat === mySeat.value)
    delta = mine?.delta ?? 0
    if (typeof mine?.nudgePending === 'number') {
      rankedLobby.setOfferPending(mine.nudgePending)
    }
    if (typeof mine?.callClaims === 'number') {
      rankedLobby.setCallClaims(mine.callClaims)
    }
    if ((mine?.nudge ?? 0) > 0) gameOverNudge.value = mine?.nudge ?? 0
    if (over.rankedOffer) {
      rankedLobby.setOfferPending(over.rankedOffer.pending)
      if (duelPrefs.rankedPrompts) {
        gameOverOfferCoins.value = over.rankedOffer.coins
      }
    }
    if (over.ranked?.packs) useCollectionStore().applyPacks(over.ranked.packs)
    if (over.lastCall) useLastCallStore().apply(over.lastCall)
    useLeagueRewardsStore().raise(over.ranked?.cosmeticsUnlocked)
    if (progress) {
      wallet.setCoins(progress.coins)
      useProgressStore().applyDuelResult(progress)
      if (progress.packs) useCollectionStore().applyPacks(progress.packs)
    } else if (mine) {
      wallet.setCoins(mine.coins)
    }
    if (over.missions) {
      useMissionsStore().apply(over.missions)
      if (typeof over.missions.coins === 'number') {
        wallet.setCoins(over.missions.coins)
      }
    }
  }
  gameOverReward.value = delta
  gameOverRanked.value = over.ranked ?? null
  gameOverProgress.value = progress
  gameOverReason.value = over.winReason ?? null
  if (over.winReason === 'exodia') {
    // The "added to your hand" list is still up for a search win. Drop it
    // so its Close tap is not the click that skips the cinematic.
    if (cardPicker.value?.viewOnly) {
      cardPicker.value = null
      duelScene()?.resolvePicker(null)
    }
    exodiaVisible.value = true
    return
  }
  gameOverVisible.value = true
}

function onExodiaDone(): void {
  exodiaVisible.value = false
  gameOverVisible.value = true
}

const myAvatar = computed(() => {
  const s = state.value
  if (s) return parseAvatarConfig(s.seats[mySeat.value]?.avatar)
  return parseAvatarConfig(player.avatar)
})

const oppAvatar = computed(() => {
  const s = state.value
  if (s) {
    const opp = (mySeat.value === 0 ? 1 : 0) as Seat
    return parseAvatarConfig(s.seats[opp]?.avatar)
  }
  if (isLocalDuel.value) return localCpuAvatar.value
  return vsOppAvatar.value
})

function medalStanding(seat: Seat): RankedStanding | null {
  const standing = state.value?.seats[seat]?.standing
  if (!standing || isPlacement(standing)) return null
  return standing
}

const myStanding = computed(() => medalStanding(mySeat.value))
const oppStanding = computed(() =>
  medalStanding((mySeat.value === 0 ? 1 : 0) as Seat),
)

const vsLeftName = computed(() => {
  if (isSpectate.value || isReplay.value) {
    return state.value?.seats[mySeat.value]?.username?.trim() || ''
  }
  return player.username
})
const vsRightName = computed(() => {
  if (isLocalDuel.value) return t('duel.dice.cpu')
  if (vsOppName.value) return vsOppName.value
  const s = state.value
  if (!s) return t('duel.opponent')
  const opp = s.seats[mySeat.value === 0 ? 1 : 0]
  return opp?.username || t('duel.opponent')
})

const rosterYou = computed(() => rosterPair.value?.[mySeat.value] ?? null)
const rosterOpp = computed(() => {
  const pair = rosterPair.value
  if (!pair) return null
  return pair[mySeat.value === 0 ? 1 : 0]
})

const diceLeftLabel = computed(() => t('duel.you'))
const diceRightLabel = computed(() => {
  if (isLocalDuel.value) return t('duel.dice.cpu')
  if (vsOppName.value) return vsOppName.value
  const s = state.value
  if (!s) return t('duel.opponent')
  const opp = s.seats[mySeat.value === 0 ? 1 : 0]
  return opp?.username || t('duel.opponent')
})

function onVsDone(): void {
  vsVisible.value = false
  if (isLocalDuel.value && !game) {
    bootDuel(duelPrefs.localFirstSeat())
    return
  }
  if (rosterPair.value) {
    rosterVisible.value = true
    return
  }
  void nextTick(() => settleDuelBoard())
}

function onRosterDone(): void {
  rosterVisible.value = false
  void nextTick(() => settleDuelBoard())
}

async function onDiceDone(result: DiceOffResult): Promise<void> {
  diceVisible.value = false
  dicePreset.value = null
  if (isLocalDuel.value && !game) {
    await nextTick()
    bootDuel(result.firstPlayer)
    return
  }
  vsVisible.value = true
}

function readSpectatorNames(payload: { names?: unknown }): string[] {
  if (!Array.isArray(payload.names)) return []
  const out: string[] = []
  for (const name of payload.names) {
    if (typeof name !== 'string') continue
    const trimmed = name.trim()
    if (!trimmed || out.includes(trimmed)) continue
    out.push(trimmed)
  }
  return out
}

const buildClient = (firstPlayer?: Seat): GameClient => {
  spectatorNames.value = []
  const mode = String(route.query.mode || 'matchmaking')
  const username = String(route.query.username || player.username || 'Player')

  if (isReplay.value && replayClient) {
    statusKey.value = null
    statusRaw.value = ''
    return replayClient
  }

  if (isSpectate.value) {
    statusKey.value = 'duel.watching'
    statusRaw.value = ''
    return new SpectateGameClient(String(route.query.code || ''), {
      admin: String(route.query.from || '') === 'ops',
    })
  }

  if (isLocalDuel.value) {
    statusKey.value = null
    statusRaw.value = ''
    return new LocalEngineClient({
      firstPlayer,
      cpuAvatar: localCpuAvatar.value,
    })
  }

  let connectMode: ConnectMode
  if (mode === 'create') {
    const invite = String(route.query.invite || '').trim()
    connectMode = {
      kind: 'create',
      username,
      vsAI: route.query.vsAI === '1',
      ...(invite ? { invite } : {}),
    }
    statusKey.value = 'duel.creating'
    statusRaw.value = ''
  } else if (mode === 'join') {
    connectMode = { kind: 'join', code: String(route.query.code || ''), username }
    statusKey.value = 'duel.joining'
    statusRaw.value = ''
  } else if (mode === 'tournament') {
    const entry = String(route.query.entry || '')
    connectMode = {
      kind: 'tournament',
      username,
      matchId: String(route.query.match || ''),
      ...(entry ? { via: entry } : {}),
    }
    statusKey.value = 'duel.tournamentWait'
    statusRaw.value = ''
  } else if (mode === 'casual') {
    connectMode = { kind: 'matchmaking', username, casual: true }
    statusKey.value = 'duel.searchingCasual'
    statusRaw.value = ''
    queueBeganAt = Date.now()
  } else if (mode === 'event') {
    connectMode = { kind: 'matchmaking', username, event: true }
    statusKey.value = 'duel.searchingEvent'
    statusRaw.value = ''
    queueBeganAt = Date.now()
  } else {
    const callId = takeStagedRankedCall()
    connectMode = callId
      ? { kind: 'matchmaking', username, callId }
      : { kind: 'matchmaking', username }
    statusKey.value = 'duel.searching'
    statusRaw.value = ''
    queueBeganAt = Date.now()
  }
  return new NetworkGameClient(connectMode)
}

function startStallWatch(): void {
  if (isSpectate.value || isReplay.value) return
    stallWatch.start(() => {
      checkBattleStall()
      checkCardFault()
      checkBoardGaps()
      duelScene()?.repairIdleField()
    })
}

let duelPageLive = true

onMounted(() => {
  duelPageLive = true
  setLiveAiKillHandler((reason) => endLocalAi(reason))
  document.addEventListener('visibilitychange', onAiCapVisible)
  window.addEventListener('keydown', onPromptHotkey, true)
  void bootMounted()
})

async function bootMounted(): Promise<void> {
  if (isEventDuel() && !useSiteStore().eventQueue) {
    await useSiteStore().refresh().catch(() => undefined)
  }
  if (!duelPageLive) return
  if (
    !isSpectate.value &&
    !isReplay.value &&
    !skipsEventDeck() &&
    !ensurePlayableDeckList()
  ) {
    void router.replace({ name: 'home', query: { needDeck: '1' } })
    return
  }
  window.addEventListener('pointerdown', primeSfx, { once: true })
  startStallWatch()
  if (String(route.query.mode || '') === 'tournament') {
    startTournamentClock()
    void tournament.refresh()
  }
  tryStartDuel()
}

function rebindTournamentClient(): void {
  teardownDuelBoard()
  resetDuelChrome()
  statusKey.value = 'duel.tournamentWait'
  startStallWatch()
  tryStartDuel()
}

watch(
  () => (String(route.query.mode || '') === 'tournament' ? String(route.query.match || '') : ''),
  (next, prev) => {
    if (!next || !prev || next === prev) return
    rebindTournamentClient()
  },
)

watch(needsRotate, (need) => {
  if (!need) tryStartDuel()
})

watch(duelCompact, (on) => {
  if (on) {
    gearOpen.value = false
    chatUi.close()
  }
  duelScene()?.syncFocusCamera(false)
  scheduleDesktopLp()
})

watch(duelSnug, () => {
  scheduleDesktopLp()
})

function shiftFocus(dir: -1 | 1): void {
  duelScene()?.shiftFocusBand(dir)
}

function confirmAiDifficulty(): void {
  aiPicked.value = true
  tryStartDuel()
}

async function bootReplay(): Promise<void> {
  if (needsRotate.value || game || replayClient) return
  const id = String(route.query.id || '')
  const token = auth.token
  if (!token || !id) {
    statusKey.value = 'duel.replay.missing'
    statusRaw.value = ''
    return
  }
  statusKey.value = 'duel.replay.loading'
  statusRaw.value = ''
  try {
    const opsReplay = String(route.query.ops || '') === '1'
    const body = opsReplay
      ? await api<DuelReplay>(
          `${adminClientPath()}/tournament-replays/${encodeURIComponent(id)}`,
          { token, headers: { 'x-admin-key': readAdminKey() } },
        )
      : await fetchReplay(token, id)
    if (body.version !== REPLAY_ENGINE_VERSION) {
      statusKey.value = 'duel.replay.stale'
      statusRaw.value = ''
      return
    }
    replayClient = new ReplayClient(body)
    replayPlaying.value = true
    bootDuel()
  } catch {
    statusKey.value = 'duel.replay.missing'
    statusRaw.value = ''
  }
}

function toggleReplay(): void {
  replayPlaying.value = !replayPlaying.value
  replayClient?.setPlaying(replayPlaying.value)
}

function stepReplay(): void {
  replayClient?.stepOnce()
}

function cycleReplaySpeed(): void {
  replaySpeed.value = replaySpeed.value === 1 ? 2 : replaySpeed.value === 2 ? 4 : 1
  duelScene()?.setReplaySpeed(replaySpeed.value)
}

function tryStartDuel(): void {
  if (needsRotate.value) return
  if (game) return
  if (isReplay.value) {
    void bootReplay()
    return
  }
  if (isLocalDuel.value) {
    statusKey.value = null
    statusRaw.value = ''
    if (!aiPicked.value) return
    if (diceVisible.value || vsVisible.value) return
    if (blocksPvpBanlist()) return
    localCpuAvatar.value = pickCpuAvatar((Date.now() ^ 0x9e3779b9) >>> 0)
    if (duelPrefs.vsAiDice) diceVisible.value = true
    else vsVisible.value = true
    return
  }
  if (blocksPvpBanlist()) return
  if (blocksEventDeck()) return
  bootDuel()
}

/** Spectate and replay do not bring the viewer's deck. */
function isEventDuel(): boolean {
  return String(route.query.mode || '') === 'event'
}

function skipsEventDeck(): boolean {
  return isEventDuel() && useSiteStore().eventQueue?.mode === 'random_deck'
}

function blocksEventDeck(): boolean {
  if (!isEventDuel() || skipsEventDeck()) return false
  const mode = useSiteStore().eventQueue?.mode
  if (!mode) return false
  const list = ensurePlayableDeckList()
  if (!list || eventOwnedDeckFits(list, mode)) return false
  statusKey.value = 'duel.error.eventDeck'
  statusRaw.value = ''
  return true
}

function bringsPlayerDeck(): boolean {
  return !isSpectate.value && !isReplay.value && !skipsEventDeck()
}

function openBanlistDialog(violations: BanlistViolation[]): void {
  banlistViolations.value = violations
  banlistDialogOpen.value = true
  statusKey.value = 'duel.error.deck_banlist'
  statusRaw.value = ''
  queueBeganAt = 0
}

function closeBanlistDialog(): void {
  banlistDialogOpen.value = false
}

function editDeckFromBanlist(): void {
  banlistDialogOpen.value = false
  void router.replace({ name: 'deck-builder' })
}

function blocksPvpBanlist(): boolean {
  if (!bringsPlayerDeck() || isEventDuel()) return false
  const deck = ensurePlayableDeckList()
  if (!deck) return false
  const violations = pvpTempBanlistViolations(deck)
  if (violations.length === 0) return false
  openBanlistDialog(violations)
  return true
}

function bootDuel(firstPlayer?: Seat): void {
  client = buildClient(firstPlayer)

  // Keep our reactive copy of the authoritative state in sync.
  client.setHandlers({
    onRoomUpdate: (room) => {
      roomCode.value = room.code
      const opp = room.players.find((p) => p.seat !== mySeat.value && !p.isAI)
      oppLinkDown.value = Boolean(state.value && opp && !opp.connected)
      const humanPvp = room.players.filter((p) => !p.isAI).length === 2
      if (humanPvp) {
        chatUi.attachDuel({
          send: (text) => client?.sendChat?.(text),
          mySeat: mySeat.value,
          peer: opp
            ? { username: opp.username, connected: opp.connected }
            : null,
        })
      } else {
        chatUi.detachDuel()
      }
      if (room.players.length < 2) {
        statusKey.value = 'duel.waitingJoin'
        statusRaw.value = ''
      }
    },
    onStart: (p) => {
      seatRename.value = null
      mySeat.value = p.viewerSeat
      if (isLocalDuel.value) clockSnap.value = null
      chatUi.setMySeat(p.viewerSeat)
      resetEventLog()
      rememberCards(p.state)
      // Hands fill from Phaser's opening-deal boardStateCb, not this snapshot.
      statusKey.value = null
      statusRaw.value = ''
      roomCode.value = null
      const opp = (p.viewerSeat === 0 ? 1 : 0) as Seat
      vsOppName.value = p.state.seats[opp]?.username || ''
      vsOppAvatar.value = parseAvatarConfig(p.state.seats[opp]?.avatar)
      if (p.dice) dicePreset.value = p.dice
      matchStarted = true
      matchBeganAt = Date.now()
      stallWatch.clearClaims()
      faultWatch.clear()
      const mode = isLocalDuel.value
        ? 'local'
        : String(route.query.mode || 'matchmaking')
      track('first_duel_started', { mode })
      if (isLocalDuel.value) noteAiDeck(matchBeganAt)
      if (queueBeganAt > 0 && (mode === 'matchmaking' || mode === 'casual' || mode === 'event')) {
        track('queue_wait', { ms: Date.now() - queueBeganAt })
        queueBeganAt = 0
      }
      if (isReplay.value) {
        rosterPair.value = null
        clockSnap.value = null
        duelScene()?.releaseOpeningDeal()
        return
      }
      if (isLocalDuel.value) {
        rosterPair.value = null
        reportLocalDuel(p.state)
        armAiCap()
        if (duelPrefs.vsAiDice) vsVisible.value = true
        else duelScene()?.releaseOpeningDeal()
        return
      }
      rosterPair.value = p.roster ?? null
      if (p.dice) diceVisible.value = true
      else vsVisible.value = true
      warmOpeningArt(p.state, p.viewerSeat)
    },
    onState: (p) => {
      noteLinkCaughtUp()
      mySeat.value = p.viewerSeat
      chatUi.setMySeat(p.viewerSeat)
      applyBoardState(p.state)
      if (!matchStarted && !isLocalDuel.value) {
        statusKey.value = null
        statusRaw.value = ''
        matchStarted = true
        matchBeganAt = Date.now()
        stallWatch.clearClaims()
        faultWatch.clear()
        hiddenNotes = 0
      }
      // A reconnect has no dice. Mount now. A fresh ranked start waits
      // until the intro has dropped its WebGL contexts.
      if (
        defersNetworkBoard() &&
        !game &&
        !diceVisible.value &&
        !vsVisible.value &&
        !rosterVisible.value
      ) {
        mountPhaserBoard(true)
      }
      publishGyGlance(true)
    },
    onEvents: (p) => {
      mySeat.value = p.viewerSeat
      chatUi.setMySeat(p.viewerSeat)
      rememberCards(p.state)
      appendEventLog(p.events)
      stallWatch.noteEvents(p.events, (uid) => {
        return cardIdByUid.get(uid) ?? findCardId(p.state, uid)
      })
      stallWatch.noteProgress()
      // Do not assign `state` here: the engine snapshot is already past the
      // animation. Trap Hole (and phase HUD) wait for Phaser's boardStateCb.
    },
    onLink: (up) => onSocketLink(up),
    onError: (msg, code, vars) => {
      if (
        code === 'not_friends' ||
        code === 'friend_offline' ||
        code === 'friend_in_duel' ||
        code === 'invalid_username'
      ) {
        statusKey.value = `duel.error.${code}`
        statusRaw.value = ''
        return
      }
      if (code === 'replayFailed') {
        showToast(t('duel.replay.stale'), 'warn')
        replayPlaying.value = false
        return
      }
      if (msg === 'auth_required' || msg.includes('auth_required')) {
        statusKey.value = 'auth.errors.expired'
        statusRaw.value = ''
        return
      }
      statusKey.value = null
      const waiting = state.value
        ? isWaitingOnOpponent(state.value, mySeat.value)
        : false
      statusRaw.value = localizeEngineError(msg, code, vars, waiting)
      if (code === 'roomGone') {
        roomLost.value = true
        noteLinkCaughtUp()
        void pullInterruptNotice(true)
        return
      }
      if (code === 'spectateDenied') return
      if (code === 'sessionMoved') {
        sessionYielded.value = true
        showToast(statusRaw.value, 'warn')
        return
      }
      if (code === 'queueClosed' || code === 'eventClosed') return
      if (code === 'deck_banlist') {
        const deck = ensurePlayableDeckList()
        const violations = deck ? pvpTempBanlistViolations(deck) : []
        if (violations.length > 0) {
          openBanlistDialog(violations)
        }
        return
      }
      if (msg && !msg.startsWith('Waiting for')) {
        const s = state.value
        const sent = client?.lastAction ?? null
        const card = s && sent ? duelActionCard(s, sent) : ''
        const pendCard = s ? duelPendingCard(s) : ''
        track('engine_error', {
          message: msg.slice(0, 120),
          ...(code ? { code } : {}),
          ...(sent ? { act: sent.type } : {}),
          ...(card ? { card } : {}),
          ...(pendCard ? { pendCard } : {}),
          ...(s
            ? {
                phase: s.phase,
                turn: s.turnPlayer === mySeat.value ? 'me' : 'opp',
                turnN: s.absoluteTurnCount,
                pending: duelPendingKind(s),
              }
            : {}),
          room: roomCode.value ?? (isLocalDuel.value ? 'local' : ''),
        })
      }
    },
    onChat: (msg) => {
      chatUi.pushDuelMessage(msg)
    },
    onChatHistory: (payload) => {
      chatUi.setDuelHistory(payload.messages)
    },
    onClock: (payload) => {
      applyClock(payload)
    },
    onSpectators: (payload) => {
      spectatorNames.value = readSpectatorNames(payload)
    },
    onGyGlance: (payload) => {
      const viewer = payload.viewer
      if (viewer !== 0 && viewer !== 1) return
      const next: [Seat | null, Seat | null] = [gyOpen.value[0], gyOpen.value[1]]
      next[viewer] = payload.open && (payload.whose === 0 || payload.whose === 1)
        ? payload.whose
        : null
      gyOpen.value = next
    },
  })

  if (!defersNetworkBoard()) mountPhaserBoard(false)
}

/**
 * Ranked and casual create the board after dice and VS release WebGL.
 * A private room keeps the board up while it waits; that canvas is already
 * drawing, so the intro does not take it.
 */
function defersNetworkBoard(): boolean {
  if (isLocalDuel.value || isReplay.value || isSpectate.value) return false
  const mode = String(route.query.mode || 'matchmaking')
  return (
    mode === 'matchmaking' ||
    mode === 'casual' ||
    mode === 'event' ||
    mode === 'tournament'
  )
}

/** Decode the opening hand during the intro, before Phaser exists. */
function warmOpeningArt(s: MatchState, seat: Seat): void {
  for (const card of s.zones[seat]?.hand ?? []) {
    if (!card.cardId) continue
    const img = new Image()
    img.decoding = 'async'
    img.src = getLocalCardImageUrl(card.cardId)
  }
}

function phoneBoard(): boolean {
  return window.matchMedia('(pointer: coarse)').matches
}

function phaserGl(): WebGLRenderingContext | null {
  if (!game || game.renderer.type !== Phaser.WEBGL) return null
  return (game.renderer as Phaser.Renderer.WebGL.WebGLRenderer).gl ?? null
}

function phaserGlLost(): boolean {
  const gl = phaserGl()
  if (!game || game.renderer.type !== Phaser.WEBGL) return false
  return !gl || gl.isContextLost() || gl.drawingBufferWidth === 0 || gl.drawingBufferHeight === 0
}

/** One row per duel, plus another if the context actually dies. */
function noteBoardPaint(when: 'settle' | 'contextlost'): void {
  if (when === 'settle') {
    if (boardPaintNoted) return
    boardPaintNoted = true
  }
  const gl = phaserGl()
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  track('board_blank', {
    when,
    renderer: !game ? 'none' : game.renderer.type === Phaser.WEBGL ? 'webgl' : 'canvas',
    lost: phaserGlLost(),
    bufferW: gl?.drawingBufferWidth ?? game?.canvas?.width ?? 0,
    bufferH: gl?.drawingBufferHeight ?? game?.canvas?.height ?? 0,
    phone: phoneBoard(),
    standalone,
    hidden: document.visibilityState === 'hidden',
    compact: duelCompact.value,
    mode: duelModeLabel(),
  })
}

function scheduleBoardPaintNote(): void {
  if (boardPaintTimer != null) window.clearTimeout(boardPaintTimer)
  boardPaintTimer = window.setTimeout(() => {
    boardPaintTimer = null
    if (phaserGlLost() && client && game && phaserType !== Phaser.CANVAS) {
      noteBoardPaint('contextlost')
      rebuildPhaserOnCanvas()
      scheduleBoardPaintNote()
      return
    }
    noteBoardPaint('settle')
  }, 800)
}

/**
 * Dice and VS have dropped their contexts. If they took the board's WebGL
 * with them, paint the same match on Canvas. A healthy canvas just deals.
 */
function settleDuelBoard(): void {
  if (phoneBoard()) phaserType = Phaser.CANVAS
  if (!game && client && defersNetworkBoard()) {
    mountPhaserBoard(false, false)
    scheduleBoardPaintNote()
    return
  }
  const lost = introGlLost || phaserGlLost()
  introGlLost = false
  if (lost && client && game && phaserType !== Phaser.CANVAS) {
    noteBoardPaint('contextlost')
    rebuildPhaserOnCanvas()
    scheduleBoardPaintNote()
    return
  }
  game?.scale.refresh()
  const scene = duelScene()
  scene?.releaseOpeningDeal()
  if (!isLocalDuel.value && scene && !scene.boardReady()) client?.requestState()
  scheduleBoardPaintNote()
}

/** Drop the board context. A leftover WebGL canvas is what paints white on Play again. */
function releaseBoardContext(): void {
  const gl = phaserGl()
  try {
    gl?.getExtension('WEBGL_lose_context')?.loseContext()
  } catch {
    /* The context is already gone. */
  }
  try {
    game?.destroy(true)
  } catch {
    /* destroy throws once the context has been lost */
  }
  game = null
  document
    .getElementById('online-game-container')
    ?.querySelectorAll('canvas')
    .forEach((node) => node.remove())
  document.getElementById('sharp-card-art')?.replaceChildren()
  document.getElementById('sharp-card-stats')?.replaceChildren()
}

function rebuildPhaserOnCanvas(): void {
  if (!client || !game || phaserType === Phaser.CANVAS) return
  phaserType = Phaser.CANVAS
  const resume = matchStarted
  scaleObserver?.disconnect()
  scaleObserver = null
  releaseBoardContext()
  mountPhaserBoard(resume)
}

/**
 * Canvas draws smooth. `antialias: false` marks the whole canvas pixelated
 * when the phone scales it up. That is not a WebGL context.
 *
 * Leave the buffer at 1024×768. Growing it pulls the playmat into the top
 * of the stage and leaves the rest black. Card faces and ATK/DEF are
 * sharpened in the page, not by resizing this canvas.
 */
function sharpenBoardCanvas(): void {
  const canvas = game?.canvas
  if (!canvas) return
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
}

function mountPhaserBoard(resume: boolean, holdOpening = !resume): void {
  if (!client) return
  phaserType = Phaser.CANVAS
  const minimalMotion = duelPrefs.motionLevel === 'minimal'
  game = new Phaser.Game({
    type: phaserType,
    width: 1024,
    height: 768,
    parent: 'online-game-container',
    // Let the Field Spell atmosphere show in the FIT letterbox and the
    // dark margins around the playmats. The buffer stays 1024×768.
    // Three.js overlays keep their own pixel-ratio cap.
    transparent: true,
    backgroundColor: '#00000000',
    disableContextMenu: true,
    // Blur used to pause the whole loop while the tab stayed "visible"
    // (another monitor, Safari). The attack animation froze and game over
    // waited behind it.
    pauseOnBlur: false,
    // One stuck touch pointer used to drop every later card tap on iPhone.
    input: { activePointers: 4 },
    render: {
      // On for every device. Off tells Phaser to pixelate this Canvas when
      // the phone scales it. The board stays Canvas either way.
      antialias: true,
      // Premultiplied alpha composites as solid white on some Android GPUs.
      premultipliedAlpha: false,
      powerPreference: 'default',
    },
    scale: { mode: Phaser.Scale.FIT, autoCenter: Phaser.Scale.CENTER_BOTH },
    fps: {
      target: minimalMotion ? 30 : 60,
      limit: minimalMotion ? 30 : 0,
    },
  })
  sharpenBoardCanvas()
  game.scale.on(Phaser.Scale.Events.RESIZE, sharpenBoardCanvas)
  if (vfxCanvas.value) {
    const vfx = bindDuelVfx(vfxCanvas.value, () => {
      if (game?.canvas) return game.canvas
      return document.querySelector(
        '#online-game-container canvas',
      ) as HTMLCanvasElement | null
    })
    if (vfx && vfxCanvas.value !== vfx.domElement) vfxCanvas.value = vfx.domElement
  }
  const parent = document.getElementById('online-game-container')
  if (parent) {
    scaleObserver = new ResizeObserver(() => {
      game?.scale.refresh()
      scheduleDesktopLp()
    })
    scaleObserver.observe(parent)
  }
  scheduleDesktopLp()

  // Inject the scene<->Vue bridges via the scene's init data so they are set
  // before the first render (no dependency on Phaser boot timing).
  game.scene.add('NetworkDuelScene', NetworkDuelScene, true, {
    client,
    hoverCb: (card: CardViewerData | null, faceDown: boolean, sleeveUrl?: string) => {
      hoveredCard.value = card
      hoveredCardFaceDown.value = faceDown
      hoveredSleeve.value = sleeveUrl ?? ''
    },
    gyCb: (cards: any[], owner: string) => {
      graveyardCards.value = cards
      graveyardOwner.value = owner
      graveyardVisible.value = true
    },
    extraCb: (cards: any[], owner: string) => {
      if (owner === 'opponent') return
      extraCards.value = cards
      extraOwner.value = owner
      extraVisible.value = true
    },
    banishedCb: (cards: any[], owner: string) => {
      banishedCards.value = cards
      banishedOwner.value = owner
      banishedVisible.value = true
    },
    gameOverCb: (over) => {
      void onMatchOver(over)
    },
    activationCb: (
      cardId: string,
      kind?: 'spell' | 'trap' | 'summon',
      foil?: string,
      turnMax?: number,
    ) => showActivation(cardId, kind ?? 'spell', foil, turnMax ?? 0),
    toastCb: (message: string, tone: DuelToastTone) => showToast(message, tone),
    offeringHandFaultCb: () => onOfferingHandFault(),
    boardStateCb: (s: MatchState) => applyBoardState(s),
    handWaitCb: (top: number | null) => {
      handWaitRatio.value = top
    },
    phaseHudCb: (s: MatchState) => applyPhaseHud(s),
    playbackCb: (on: boolean) => {
      playbackLocked.value = on
      if (!on) replayClient?.onBoardIdle()
    },
    selectingCb: (on: boolean) => {
      selectingTarget.value = on
    },
    pickSideCb: (side: DuelPickSide | null) => {
      pickSide.value = side
    },
    menuCb: (menu: FieldMenuPayload | null) => {
      fieldMenu.value = menu
    },
    pickerCb: (req: CardPickerRequest | null) => {
      cardPicker.value = req
    },
    positionCb: (card, done) => {
      positionCard.value = card
      positionVisible.value = true
      positionDone = done
    },
    focusBandCb: (band: FocusBand) => {
      focusBand.value = band
    },
    lpPunchCb: (mine: boolean, durationMs?: number) =>
      punchCompactLp(mine, durationMs),
    lpHudCb: (tick) => onCompactLpTick(tick),
    screenShakeCb: (durationMs) => onBoardShake(durationMs),
    holdOpeningDeal: holdOpening,
    resumeBoard: resume,
    glFallbackCb: () => {
      noteBoardPaint('contextlost')
      if (introLocked.value) {
        introGlLost = true
        return
      }
      window.setTimeout(() => rebuildPhaserOnCanvas(), 0)
    },
  })
  duelScene()?.setModalInputBlocked(boardModalOpen.value)

  requestAnimationFrame(() => {
    game?.scale.refresh()
    const scene = game?.scene.getScene('NetworkDuelScene') as NetworkDuelScene | undefined
    scene?.beginMatch()
  })
}

/** Drop the board and the socket. The page, the fullscreen lock, and the listeners stay. */
function teardownDuelBoard(): void {
  if (matchStarted && !matchFinished) {
    const mode = isLocalDuel.value
      ? 'local'
      : String(route.query.mode || 'matchmaking')
    track('duel_abandoned', {
      mode,
      ms: matchBeganAt ? Date.now() - matchBeganAt : 0,
    })
  }
  clearAiCap()
  aiKillReason = null
  stallWatch.stop()
  clearLiveAiDuel()
  stopQueueClock()
  chatUi.detachDuel()
  clearLinkDownTimer()
  if (activationTimer) clearTimeout(activationTimer)
  activationTimer = null
  if (activationCountTimer) {
    clearInterval(activationCountTimer)
    activationCountTimer = null
  }
  if (toastTimer) clearTimeout(toastTimer)
  toastTimer = null
  if (fieldMenuHoldTimer) clearTimeout(fieldMenuHoldTimer)
  fieldMenuHoldTimer = null
  if (lpPunchMineTimer) clearTimeout(lpPunchMineTimer)
  lpPunchMineTimer = null
  if (lpPunchOppTimer) clearTimeout(lpPunchOppTimer)
  lpPunchOppTimer = null
  if (lpFloatMineTimer) clearTimeout(lpFloatMineTimer)
  lpFloatMineTimer = null
  if (lpFloatOppTimer) clearTimeout(lpFloatOppTimer)
  lpFloatOppTimer = null
  if (boardShakeTimer) clearTimeout(boardShakeTimer)
  boardShakeTimer = null
  if (shuffleCoolTimer) clearTimeout(shuffleCoolTimer)
  shuffleCoolTimer = null
  scaleObserver?.disconnect()
  scaleObserver = null
  if (boardPaintTimer != null) {
    window.clearTimeout(boardPaintTimer)
    boardPaintTimer = null
  }
  // Keep the effects context. Dropping it paints that canvas white, and
  // Play again builds the next duel on the same element, over the board.
  parkDuelVfx()
  const leaving = client
  client = null
  leaving?.dropHandlers()
  leaving?.dispose()
  releaseBoardContext()
  replayClient = null
}

/** Match chrome back to a fresh visit, without leaving the duel route. */
function resetDuelChrome(): void {
  matchStarted = false
  matchFinished = false
  matchBeganAt = 0
  hiddenNotes = 0
  queueBeganAt = 0
  state.value = null
  mySeat.value = 0
  clockSnap.value = null
  shownMyLp.value = 8000
  shownOppLp.value = 8000
  lpLitMine.value = false
  lpLitOpp.value = false
  lpFloatMine.value = null
  lpFloatOpp.value = null
  lpPunchMine.value = false
  lpPunchOpp.value = false
  boardShaking.value = false
  boardShakeMs.value = 0
  hudPhase.value = null
  hudTurnPlayer.value = null
  hudTurnCount.value = 1
  hudAbsTurn.value = 1
  playbackLocked.value = false
  statusKey.value = null
  statusRaw.value = ''
  sessionYielded.value = false
  roomLost.value = false
  linkDown.value = false
  oppLinkDown.value = false
  queueElapsedSec.value = 0
  roomCode.value = null
  hoveredCard.value = null
  hoveredCardFaceDown.value = false
  hoveredSleeve.value = ''
  graveyardVisible.value = false
  graveyardCards.value = []
  gyOpen.value = [null, null]
  sentGyWhose = null
  extraVisible.value = false
  extraCards.value = []
  banishedVisible.value = false
  banishedCards.value = []
  gameOverVisible.value = false
  gameOverWinner.value = null
  gameOverHeadline.value = ''
  gameOverDetail.value = ''
  gameOverReason.value = null
  gameOverReward.value = 0
  gameOverNudge.value = 0
  gameOverOfferCoins.value = 0
  gameOverRanked.value = null
  gameOverProgress.value = null
  exodiaVisible.value = false
  surrenderAsk.value = false
  leaveAsk.value = false
  banlistDialogOpen.value = false
  banlistViolations.value = []
  positionVisible.value = false
  positionCard.value = null
  positionDone = null
  activationCardId.value = null
  activationTurnsMax.value = 0
  activationTurnShown.value = 0
  toastMessage.value = ''
  handWaitRatio.value = null
  selectingTarget.value = false
  pickSide.value = null
  cardPicker.value = null
  gearOpen.value = false
  focusBand.value = 'hand'
  vsVisible.value = false
  rosterVisible.value = false
  rosterPair.value = null
  diceVisible.value = false
  aiPicked.value = false
  dicePreset.value = null
  vsOppName.value = ''
  introGlLost = false
  boardPaintNoted = false
  if (boardPaintTimer != null) {
    window.clearTimeout(boardPaintTimer)
    boardPaintTimer = null
  }
  shuffleCooling.value = false
  rankedCallPending.value = false
  resetEventLog()
  stallWatch.clearClaims()
  faultWatch.clear()
  fieldMenu.value = null
  if (fieldMenuHoldTimer) {
    clearTimeout(fieldMenuHoldTimer)
    fieldMenuHoldTimer = null
  }
  fieldMenuHold.value = false
}

/**
 * Start the next duel in this same document. A reload would drop fullscreen,
 * and the new page cannot ask for it again without a tap.
 */
async function restartDuelSession(next?: {
  mode: string
  username: string
}): Promise<void> {
  if (next) {
    await router.replace({
      path: '/online-duel',
      query: { mode: next.mode, username: next.username },
    })
    if (route.name !== 'online-duel') return
  }
  teardownDuelBoard()
  resetDuelChrome()
  // The old WebGL context turns the next board white if it is still alive
  // when Play again creates one. Wait until the canvas is actually gone.
  await nextTick()
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve())
  })
  if (
    !isSpectate.value &&
    !isReplay.value &&
    !skipsEventDeck() &&
    !ensurePlayableDeckList()
  ) {
    void router.replace({ name: 'home', query: { needDeck: '1' } })
    return
  }
  startStallWatch()
  tryStartDuel()
}

onUnmounted(() => {
  duelPageLive = false
  setLiveAiKillHandler(null)
  document.removeEventListener('visibilitychange', onAiCapVisible)
  window.removeEventListener('keydown', onPromptHotkey, true)
  stopTournamentClock()
  teardownDuelBoard()
  unbindDuelVfx()
})
</script>

<style scoped>
.watch-kicker {
  margin: 0;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #fde68a;
}

.spectator-list {
  align-self: stretch;
  margin-right: 8px;
  max-height: 96px;
  overflow-y: auto;
  padding: 6px 8px;
  border-radius: 8px;
  border: 1px solid rgba(232, 197, 71, 0.28);
  background: rgba(12, 14, 24, 0.9);
}

.spectator-list-title {
  margin: 0 0 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #fde68a;
}

.spectator-list ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

.spectator-list li {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  line-height: 1.35;
  color: #f4f0ea;
}

.online-duel-view.snug .spectator-list {
  max-height: 72px;
  padding: 4px 6px;
}

.spectator-eye {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  box-sizing: border-box;
  min-height: 26px;
  padding: 0 6px;
  border-radius: 6px;
  border: 1px solid rgba(232, 197, 71, 0.4);
  background: #0c0e18;
  color: #fde68a;
  font-size: 12px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  pointer-events: none;
}

.online-duel-view {
  --viewer-rail-width: 280px;
  --viewer-rail: calc(8px + var(--viewer-rail-width));
  position: fixed;
  top: var(--app-nav-offset, 64px);
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  background: #0c1018;
  transition: background-color 0.85s ease;
}

.online-duel-view.field-forest {
  background: #07140c;
}

.online-duel-view.field-umi {
  background: #06141f;
}

.online-duel-view.field-yami {
  background: #0c0714;
}

.online-duel-view.field-sogen {
  background: #12150a;
}

.online-duel-view.field-wasteland {
  background: #1a0f08;
}

.online-duel-view.field-mountain {
  background: #0c1218;
}

.card-viewer-rail {
  flex: 0 0 var(--viewer-rail-width);
  width: var(--viewer-rail-width);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  overflow-y: auto;
  padding: 8px 0 8px 8px;
  z-index: 100;
  gap: 8px;
  background: rgba(8, 10, 16, 0.78);
}

.ranked-call-rail-slot {
  position: sticky;
  bottom: 8px;
  z-index: 5;
  flex-shrink: 0;
  width: calc(100% - 8px);
  margin-top: auto;
}

.online-duel-view.compact .card-viewer-rail {
  display: none;
}

.online-duel-view.snug {
  --viewer-rail-width: 220px;
}

.online-duel-view.snug .card-viewer-rail {
  gap: 4px;
  padding-top: 4px;
}

.online-duel-view.snug .sfx-toggle,
.online-duel-view.snug .duel-leave {
  align-self: stretch;
  margin-right: 8px;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  line-height: 1.2;
}

.online-duel-view.snug .duel-leave {
  margin-bottom: 0;
}

.online-duel-view.snug .viewer-tabs {
  margin-top: 2px;
}

.online-duel-view.snug .viewer-tab {
  min-height: 24px;
  font-size: 10px;
}

.online-duel-view.snug .card-viewer-rail :deep(.rail-response-chip.mode-chip) {
  padding: 4px 8px;
}

.online-duel-view.snug .desktop-lp-avatar {
  max-width: 200px;
}

.card-viewer-text-rail {
  display: none;
}

.online-duel-view.compact .card-viewer-text-rail {
  position: relative;
  display: flex;
  flex: 0 0 var(--viewer-text-width, 168px);
  width: var(--viewer-text-width, 168px);
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  padding: 6px 8px 6px 4px;
  z-index: 1;
  background: rgba(8, 10, 16, 0.78);
}

.online-duel-view.compact .card-viewer-text-rail.stowed {
  flex: 0 0 auto;
  width: auto;
  max-width: 88px;
  overflow: visible;
}

.online-duel-view.compact .card-viewer-text-rail.stowed .viewer-thumb-tools {
  position: relative;
  top: auto;
  left: auto;
  right: auto;
  max-height: none;
  flex-direction: column;
  flex-wrap: nowrap;
}

.viewer-thumb-tools {
  position: absolute;
  top: 6px;
  left: 48px;
  right: 4px;
  z-index: 5;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  align-content: flex-start;
  gap: 4px;
  max-height: 58px;
  overflow: hidden;
  pointer-events: auto;
}

.online-duel-view.compact .card-viewer-text-rail :deep(.card-viewer) {
  width: 100% !important;
  height: 100%;
  gap: 6px;
}

.online-duel-view.compact .card-viewer-text-rail :deep(.card-viewer-image) {
  width: 40px;
  height: 58px;
  flex: 0 0 auto;
  border-radius: 4px;
}

.online-duel-view.compact .card-viewer-text-rail :deep(.card-viewer-detail) {
  width: 100% !important;
  min-height: 0;
  padding: 8px 8px 6px;
  gap: 4px;
  overflow: hidden;
}

.online-duel-view.compact .card-viewer-text-rail :deep(.card-viewer-name),
.online-duel-view.compact .card-viewer-text-rail :deep(.card-viewer-foil),
.online-duel-view.compact .card-viewer-text-rail :deep(.card-viewer-level),
.online-duel-view.compact .card-viewer-text-rail :deep(.card-viewer-meta),
.online-duel-view.compact .card-viewer-text-rail :deep(.card-viewer-stats),
.online-duel-view.compact .card-viewer-text-rail :deep(.card-viewer-typeline),
.online-duel-view.compact .card-viewer-text-rail :deep(.card-viewer-desc),
.online-duel-view.compact .card-viewer-text-rail :deep(.card-viewer-placeholder) {
  font-size: 12px;
  line-height: 16px;
}

.online-duel-view.compact .card-viewer-text-rail :deep(.card-viewer-desc) {
  --fusion-size: 12px;
  --fusion-leading: 16px;
  margin-top: 2px;
  padding-top: 6px;
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  pointer-events: auto;
}

.online-duel-view.compact .card-viewer-text-rail :deep(.card-viewer-desc.is-marked) {
  font-size: 0;
  line-height: 0;
}

.viewer-tabs {
  display: flex;
  width: calc(100% - 8px);
  margin: 0 8px 0 0;
  padding: 3px;
  border-radius: 10px;
  background: rgba(8, 10, 18, 0.72);
  border: 1px solid rgba(232, 197, 71, 0.22);
}

.viewer-tab {
  flex: 1;
  min-height: 32px;
  padding: 0 6px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #94a3b8;
  font: inherit;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  cursor: pointer;
}

.viewer-tab:hover {
  color: #fde68a;
}

.viewer-tab.active {
  background: rgba(232, 197, 71, 0.16);
  color: #fde68a;
}

.viewer-panel {
  width: 100%;
}

.viewer-panel-card {
  display: flex;
  justify-content: center;
}

.viewer-panel-log {
  flex: 1 1 auto;
  min-height: 140px;
  width: calc(100% - 8px);
  margin-right: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sfx-toggle {
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid rgba(232, 197, 71, 0.32);
  background: rgba(12, 14, 24, 0.9);
  color: #f4f0ea;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.03em;
  cursor: pointer;
  transition:
    border-color 0.18s ease,
    color 0.18s ease,
    background-color 0.18s ease;
}

.sfx-toggle:hover {
  border-color: #e8c547;
  color: #fde68a;
}

.rail-response-chip {
  width: calc(100% - 16px);
}

.card-viewer-rail :deep(.rail-response-chip.mode-chip) {
  width: calc(100% - 16px);
  min-width: 0;
  padding: 8px 12px;
}

.sfx-toggle.danger {
  border-color: rgba(248, 113, 113, 0.45);
  color: #fecaca;
}

.sfx-toggle.danger:hover {
  border-color: #f87171;
  color: #fecaca;
  background: rgba(248, 113, 113, 0.12);
}

.duel-leave {
  align-self: stretch;
  margin: 0 8px 4px;
  text-align: center;
}

.surrender-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.72);
}

.surrender-panel {
  width: min(360px, calc(100% - 32px));
  padding: 24px 22px;
  border-radius: 12px;
  border: 2px solid #facc15;
  background: #0d0d1a;
  color: #e5e7eb;
  text-align: center;
}

.surrender-panel p {
  margin: 0 0 18px;
  font-size: 15px;
  line-height: 1.4;
}

.surrender-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.board-stage {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  height: 100%;
  z-index: 1;
  background: transparent;
  transition: box-shadow 0.85s ease;
}

.online-duel-view.field-forest .board-stage {
  box-shadow: inset 0 0 90px rgba(34, 197, 94, 0.28);
}

.online-duel-view.field-umi .board-stage {
  box-shadow: inset 0 0 90px rgba(14, 165, 233, 0.32);
}

.online-duel-view.field-yami .board-stage {
  box-shadow: inset 0 0 90px rgba(147, 51, 234, 0.32);
}

.online-duel-view.field-sogen .board-stage {
  box-shadow: inset 0 0 90px rgba(202, 138, 4, 0.28);
}

.online-duel-view.field-wasteland .board-stage {
  box-shadow: inset 0 0 90px rgba(180, 83, 9, 0.3);
}

.online-duel-view.field-mountain .board-stage {
  box-shadow: inset 0 0 90px rgba(148, 163, 184, 0.28);
}

.board-stage.duel-shaking {
  animation: duel-board-shake var(--duel-shake-ms, 420ms) linear;
}

@keyframes duel-board-shake {
  0%,
  100% {
    transform: translate3d(0, 0, 0);
  }
  12% {
    transform: translate3d(-10px, 6px, 0);
  }
  24% {
    transform: translate3d(11px, -5px, 0);
  }
  36% {
    transform: translate3d(-9px, -7px, 0);
  }
  48% {
    transform: translate3d(10px, 4px, 0);
  }
  60% {
    transform: translate3d(-7px, 8px, 0);
  }
  72% {
    transform: translate3d(8px, -6px, 0);
  }
  84% {
    transform: translate3d(-5px, 3px, 0);
  }
}

#online-game-container {
  /* Fill the leftover column; Phaser FIT scales the 1024×768 board inside.
     --board-nudge slides it right on short laptop windows so the LP plates
     stay in the left gutter instead of under the card rail. */
  width: calc(100% - var(--board-nudge, 0px));
  margin-left: auto;
  height: 100%;
  position: relative;
  z-index: 1;
  background: transparent;
  /* iOS otherwise treats a card tap as a scroll and drops it. Phase buttons
     already opt out; the canvas did not, so summon/set was hit-and-miss. */
  touch-action: none;
}

#online-game-container :deep(canvas) {
  position: relative;
  z-index: 1;
  background: transparent;
  touch-action: none;
}

.sharp-card-layer {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.sharp-card-layer.art {
  z-index: 0;
}

.sharp-card-layer.stats {
  z-index: 2;
}

.sharp-card-layer.art :deep(img),
.sharp-card-layer.stats :deep(img.sharp-card-img) {
  position: absolute;
  display: block;
  object-fit: fill;
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;
}

.sharp-card-layer.stats :deep(.sharp-foil-host) {
  position: absolute;
  overflow: visible;
  pointer-events: none;
  transform-origin: center center;
}

.sharp-card-layer.stats :deep(.sharp-stat-box) {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.8);
  font-family: Roboto, system-ui, sans-serif;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  line-height: 1.05;
  white-space: nowrap;
  pointer-events: none;
  text-shadow: 0 1px 1px #000;
  border-radius: 2px;
}

.sharp-card-layer.stats :deep(.sharp-stat-box.hand) {
  flex-direction: column;
}

.sharp-card-layer.stats :deep(.sharp-stat-box.field) {
  flex-direction: row;
  gap: 0.25em;
  padding: 0 0.35em;
}

.sharp-card-layer.stats :deep(.sharp-stat-box .tag) {
  font-size: 0.73em;
  margin-right: 0.2em;
}

.sharp-card-layer.stats :deep(.sharp-stat-box .slash) {
  color: #fff;
}

.hand-wait {
  position: absolute;
  left: 50%;
  z-index: 30;
  transform: translate(-50%, 6px);
  max-width: min(420px, 88%);
  margin: 0;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid rgba(253, 224, 71, 0.55);
  background: rgba(8, 10, 16, 0.94);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.5);
  color: #fde68a;
  font-family: Roboto, system-ui, sans-serif;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.25;
  text-align: center;
  pointer-events: none;
}

.online-duel-view.compact .hand-wait {
  max-width: min(280px, 70%);
  padding: 6px 10px;
  font-size: 14px;
}

.duel-vfx {
  /* Size/position are pinned to the Phaser canvas in DuelVfx (FIT letterbox).
     Above the phase-manager HUD (150) so attack lunges and other flights
     pass over it instead of under it. pointer-events stay none so the HUD
     remains clickable. Below PhaseBanner (175) and prompt overlays (220). */
  position: absolute;
  pointer-events: none;
  z-index: 160;
}

.game-container-blocked,
.game-container-blocked :deep(canvas) {
  pointer-events: none !important;
}

.duel-hud-cluster {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 150;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: auto;
}

.desktop-side-stack {
  position: absolute;
  left: 12px;
  z-index: 28;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  max-width: calc(100% - 16px);
  pointer-events: none;
}

.desktop-side-stack.opp {
  top: 12px;
}

.desktop-side-stack.me {
  bottom: 12px;
}

.seat-wait {
  position: absolute;
  z-index: 2;
  display: block;
  margin: 0;
  max-width: min(280px, 34vw);
  padding: 9px 14px;
  border-radius: 10px;
  border: 1px solid rgba(253, 224, 71, 0.55);
  background: rgba(8, 10, 16, 0.94);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.5);
  color: #fde68a;
  font-family: Roboto, system-ui, sans-serif;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.25;
  text-align: center;
  pointer-events: none;
}

.desktop-side-stack .seat-wait {
  top: calc(100% + 6px);
  left: 0;
}

.desktop-side-stack.me .seat-wait {
  top: auto;
  bottom: calc(100% + 6px);
}

.online-duel-view.compact .seat-wait {
  left: calc(100% + 10px);
  top: auto;
  bottom: 0;
  max-width: min(360px, 72vw);
  padding: 12px 16px;
  font-size: 24px;
  line-height: 1.15;
  text-align: left;
}

.board-stage.lp-anchored .desktop-side-stack {
  left: var(--lp-edge-x);
  bottom: auto;
  transform: translateX(calc(-100% - 12px));
}

.board-stage.lp-anchored .desktop-side-stack.opp {
  top: var(--lp-opp-top);
}

.board-stage.lp-anchored .desktop-side-stack.me {
  top: var(--lp-me-bottom);
  transform: translate(calc(-100% - 12px), -100%);
}

.desktop-turn-dock {
  pointer-events: none;
}

.desktop-lp-avatar {
  position: relative;
  z-index: 28;
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  gap: 2px 8px;
  width: max-content;
  max-width: 240px;
  min-width: 40px;
  padding: 4px 8px 4px 4px;
  border-radius: 8px;
  overflow: visible;
  pointer-events: none;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.55));
  transition: background-color var(--md-motion-short) var(--md-motion-standard),
    box-shadow var(--md-motion-short) var(--md-motion-standard);
}

.desktop-lp-face {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.duel-seat-rank {
  font-size: 16px;
  line-height: 1;
}

.duel-seat-rank.compact {
  font-size: 13px;
}

.desktop-lp-score {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 0;
  min-width: 0;
  line-height: 1.1;
}

.desktop-lp-avatar.hit,
.desktop-lp-avatar.heal {
  background: #050505;
  box-shadow:
    inset 0 1px 0 rgba(209, 213, 219, 0.28),
    0 0 0 1px #6b7280;
}

.desktop-lp .compact-lp-cap {
  order: -1;
  flex: 1 0 100%;
  max-width: 100%;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1.2;
  color: #f4f0ea;
  text-align: left;
}

.desktop-lp .compact-lp-val {
  font-size: 26px;
  transform-origin: left center;
}

.desktop-lp-avatar.me .compact-lp-val {
  color: #4ade80;
}

.desktop-lp-avatar.opp .compact-lp-val {
  color: #f87171;
}

.desktop-lp .compact-lp-delta {
  font-size: 52px;
}

@media (prefers-reduced-motion: reduce) {
  .desktop-lp-avatar {
    transition: none;
  }

  .desktop-lp .compact-lp-val,
  .desktop-lp .compact-lp-val.punch,
  .desktop-lp .compact-lp-val.hit,
  .desktop-lp .compact-lp-val.heal,
  .desktop-lp .compact-lp-val.punch.hit,
  .desktop-lp .compact-lp-val.punch.heal,
  .desktop-lp .compact-lp-delta {
    animation: none;
  }
}

/* Stay fully visible during a prompt; clicks go to the overlay, not the HUD. */
.phase-control-locked {
  pointer-events: none;
}

.compact-focus-rail {
  flex: 0 0 auto;
  width: 64px;
  max-height: 100%;
  min-height: 0;
  overflow: visible;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 6px;
  padding: 4px 4px 4px 0;
  z-index: 150;
  pointer-events: auto;
  background: rgba(8, 10, 16, 0.55);
}

.compact-lp {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 2px;
  overflow: visible;
}

.compact-lp-me,
.compact-lp-opp {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  line-height: 1.1;
  padding: 4px 2px;
  border-radius: 8px;
  overflow: visible;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}

.compact-lp-me.hit,
.compact-lp-opp.hit,
.compact-lp-me.heal,
.compact-lp-opp.heal {
  background: #050505;
  box-shadow:
    inset 0 1px 0 rgba(209, 213, 219, 0.28),
    0 0 0 1px #6b7280;
}

.compact-lp-cap {
  max-width: 56px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #94a3b8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.compact-opp-line,
.compact-me-line {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  max-width: 56px;
}

.compact-opp-line .compact-lp-cap {
  max-width: 34px;
}

.compact-turn {
  flex-shrink: 0;
  min-width: 14px;
  padding: 0 3px;
  border-radius: 4px;
  background: rgba(253, 230, 138, 0.16);
  color: #fde68a;
  font-size: 9px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
  line-height: 1.3;
}

.compact-turn.opponent {
  background: rgba(248, 113, 113, 0.18);
  color: #fecaca;
}

.compact-lp-stack {
  position: relative;
  display: flex;
  justify-content: center;
  min-height: 24px;
  overflow: visible;
}

.compact-lp-val {
  position: relative;
  isolation: isolate;
  font-size: 20px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  transform-origin: center top;
}

.compact-lp-val.hit::before,
.compact-lp-val.heal::before,
.compact-lp-val.punch::before {
  content: '';
  position: absolute;
  inset: -34% -30%;
  z-index: -1;
  pointer-events: none;
  border-radius: 4px;
  background: #000000;
  border: 1px solid #9ca3af;
  box-shadow:
    inset 0 1px 0 rgba(229, 231, 235, 0.55),
    inset 0 -2px 0 rgba(0, 0, 0, 0.9),
    inset 1px 0 0 rgba(156, 163, 175, 0.28),
    inset -1px 0 0 rgba(0, 0, 0, 0.55),
    0 0 0 1px #4b5563,
    0 1px 0 #d1d5db,
    0 2px 0 #6b7280,
    0 4px 8px rgba(0, 0, 0, 0.55);
}

.compact-lp-val.punch {
  z-index: 20;
  animation: compact-lp-punch var(--lp-punch-ms, 1.1s) ease-out;
}

.compact-lp-val.hit {
  animation: compact-lp-blink-hit 0.14s step-end infinite;
}

.compact-lp-val.heal {
  animation: compact-lp-blink-heal 0.14s step-end infinite;
}

.compact-lp-val.punch.hit {
  animation:
    compact-lp-punch var(--lp-punch-ms, 1.1s) ease-out,
    compact-lp-blink-hit 0.14s step-end infinite;
}

.compact-lp-val.punch.heal {
  animation:
    compact-lp-punch var(--lp-punch-ms, 1.1s) ease-out,
    compact-lp-blink-heal 0.14s step-end infinite;
}

.compact-lp-delta {
  position: absolute;
  left: 50%;
  bottom: 100%;
  z-index: 40;
  margin-bottom: 4px;
  font-size: 40px;
  font-weight: 900;
  font-style: italic;
  letter-spacing: -0.04em;
  line-height: 1;
  pointer-events: none;
  white-space: nowrap;
}

.compact-lp-delta.heal {
  color: #4ade80;
  -webkit-text-stroke: 2px #052e16;
  paint-order: stroke fill;
  text-shadow:
    0 0 10px #22c55e,
    0 0 22px #16a34a,
    3px 3px 0 #14532d;
  animation: compact-lp-delta 1.4s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

.compact-lp-delta.hit {
  color: #f87171;
  -webkit-text-stroke: 2px #450a0a;
  paint-order: stroke fill;
  text-shadow:
    0 0 10px #ef4444,
    0 0 22px #b91c1c,
    3px 3px 0 #7f1d1d;
  animation: compact-lp-delta 1.4s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

@keyframes compact-lp-delta {
  0% {
    opacity: 0;
    transform: translateX(-50%) translateY(10px) scale(0.35);
  }
  18% {
    opacity: 1;
    transform: translateX(-50%) translateY(0) scale(1.35);
  }
  32% {
    opacity: 1;
    transform: translateX(-50%) translateY(-6px) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateX(-50%) translateY(-46px) scale(1.15);
  }
}

@keyframes compact-lp-punch {
  0% {
    transform: scale(1);
  }
  14% {
    transform: scale(2.2);
  }
  50% {
    transform: scale(1.45);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes compact-lp-blink-hit {
  0%,
  49% {
    color: #ffffff;
    text-shadow:
      0 0 6px rgba(255, 255, 255, 0.9),
      0 1px 0 #000;
  }
  50%,
  100% {
    color: #ef4444;
    text-shadow:
      0 0 8px rgba(248, 113, 113, 0.95),
      0 1px 0 #000;
  }
}

@keyframes compact-lp-blink-heal {
  0%,
  49% {
    color: #ffffff;
    text-shadow:
      0 0 6px rgba(255, 255, 255, 0.9),
      0 1px 0 #000;
  }
  50%,
  100% {
    color: #4ade80;
    text-shadow:
      0 0 8px rgba(74, 222, 128, 0.95),
      0 1px 0 #000;
  }
}

.compact-lp-me .compact-lp-val {
  color: #4ade80;
}

.compact-lp-opp .compact-lp-val {
  color: #f87171;
}

.compact-focus-rail :deep(.phase-control) {
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  padding: 4px;
  margin: 0;
}

.compact-focus-rail :deep(.phase-button) {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-width: 0;
  min-height: 44px;
  padding: 6px 2px;
  font-size: 10px;
  line-height: 1.15;
  white-space: normal;
}

.compact-focus-rail :deep(.current-player-indicator) {
  position: static;
  transform: none;
  margin-top: 2px;
  font-size: 8px;
}

.focus-arrows {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 6px;
}

.focus-arrow {
  min-width: 44px;
  min-height: 44px;
  flex-shrink: 0;
  padding: 0;
  border-radius: 8px;
  border: 1px solid rgba(232, 197, 71, 0.32);
  background: rgba(12, 14, 24, 0.9);
  color: #fde68a;
  font-size: 16px;
  cursor: pointer;
}

.focus-arrow:disabled {
  opacity: 0.35;
  cursor: default;
}

.focus-arrow:not(:disabled):hover {
  border-color: #e8c547;
}

.focus-arrow:not(:disabled):active {
  background: rgba(232, 197, 71, 0.16);
}

.link-banner {
  display: block;
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 280;
  transform: translate(-50%, -50%);
  max-width: min(420px, calc(100% - 32px));
  margin: 0;
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid rgba(232, 197, 71, 0.4);
  background: rgba(12, 14, 24, 0.92);
  color: #fde68a;
  font-size: 16px;
  font-weight: 700;
  text-align: center;
  pointer-events: none;
}

.online-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding:
    max(24px, env(safe-area-inset-top))
    16px
    max(24px, env(safe-area-inset-bottom));
  overflow-y: auto;
  background: rgba(15, 23, 42, 0.85);
  color: #e2e8f0;
  z-index: 300;
}

.online-overlay-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: min(440px, 100%);
  min-width: 0;
}

/* Phone only. Same query as MENUS_COMPACT_MQ in compactLayout.ts. */
@media (max-width: 720px), (max-height: 500px) and (max-width: 1100px) {
  .online-overlay-queue .online-back {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
  }

  .online-overlay-queue .online-overlay-body {
    box-sizing: border-box;
    width: min(440px, calc(100% - 104px));
    margin-left: 104px;
  }
}

.online-status {
  font-size: 22px;
  font-weight: 600;
  text-align: center;
}

.tournament-clock {
  margin: 0;
  font-size: 40px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
  color: #fde68a;
}

.online-code strong {
  letter-spacing: 4px;
  color: #60a5fa;
}

.online-back {
  padding: 8px 20px;
  border-radius: 8px;
  border: 1px solid rgba(232, 197, 71, 0.4);
  background: rgba(12, 14, 24, 0.92);
  color: #fde68a;
  font-weight: 700;
  cursor: pointer;
}

.online-back:hover {
  border-color: #e8c547;
}

/* Activation flash: centered on the board column, above the phase HUD.
   pointer-events is not inherited, so every descendant must opt out too —
   otherwise the big card eats taps on the graveyard picker underneath
   (Monster Reborn on a phone, while the hide timer is still running). */
.activation-overlay,
.activation-overlay :deep(*) {
  pointer-events: none;
}

.activation-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 400;
}

.activation-card-wrap {
  position: relative;
  width: 260px;
  max-width: 40vw;
  height: calc(260px * 614 / 421);
  overflow: visible;
  animation: activation-pop 1.2s ease-out forwards;
}

.online-duel-view.compact .activation-card-wrap {
  width: min(168px, 38vh);
  max-width: none;
  height: calc(min(168px, 38vh) * 614 / 421);
}

.activation-card {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  border-radius: 10px;
  box-shadow: 0 0 40px rgba(96, 165, 250, 0.7), 0 8px 24px rgba(0, 0, 0, 0.6);
}

.activation-destello {
  position: absolute;
  inset: -18%;
  z-index: 5;
  pointer-events: none;
  display: grid;
  place-items: center;
  animation: activation-destello-life 0.5s ease-out both;
}

.activation-destello-star {
  grid-area: 1 / 1;
  width: 78%;
  height: 78%;
  background: radial-gradient(
    circle,
    #fff 0%,
    rgba(255, 255, 255, 0.9) 10%,
    transparent 42%
  );
  clip-path: polygon(
    50% 0%,
    56% 44%,
    100% 50%,
    56% 56%,
    50% 100%,
    44% 56%,
    0% 50%,
    44% 44%
  );
  filter: drop-shadow(0 0 16px #fff) drop-shadow(0 0 28px rgba(255, 255, 255, 0.85));
  mix-blend-mode: screen;
}

.activation-destello-star.diag {
  width: 56%;
  height: 56%;
  transform: rotate(45deg);
  opacity: 0.9;
}

.activation-trap .activation-card {
  box-shadow: 0 0 40px rgba(248, 113, 113, 0.75), 0 8px 24px rgba(0, 0, 0, 0.6);
}

.activation-summon .activation-card {
  box-shadow: 0 0 44px rgba(251, 191, 36, 0.8), 0 8px 24px rgba(0, 0, 0, 0.6);
}

.activation-foil-rare .activation-card {
  box-shadow: 0 0 44px rgba(203, 213, 225, 0.85), 0 8px 24px rgba(0, 0, 0, 0.6);
}

.activation-foil-super .activation-card {
  box-shadow: 0 0 48px rgba(250, 204, 21, 0.9), 0 8px 24px rgba(0, 0, 0, 0.6);
}

.activation-foil-ultra .activation-card {
  box-shadow: 0 0 52px rgba(217, 119, 6, 0.95), 0 8px 24px rgba(0, 0, 0, 0.6);
}

.activation-foil-helios .activation-card {
  box-shadow: 0 0 56px rgba(251, 191, 36, 1), 0 8px 24px rgba(0, 0, 0, 0.6);
}

.activation-foil-nightglass .activation-card {
  box-shadow:
    0 0 58px rgba(125, 211, 252, 1),
    0 0 28px rgba(139, 92, 246, 0.7),
    0 8px 24px rgba(0, 0, 0, 0.6);
}

.activation-foil-stormwake .activation-card {
  box-shadow: 0 0 52px rgba(251, 113, 133, 0.95), 0 8px 24px rgba(0, 0, 0, 0.6);
}

.activation-turn-counter {
  position: absolute;
  inset: 0;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.activation-turn-digit {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.72);
  border: 3px solid #facc15;
  color: #fde047;
  font-size: 52px;
  font-weight: 800;
  line-height: 1;
  text-shadow: 0 2px 0 #000;
  animation: activation-turn-pop 0.28s ease-out;
}

@keyframes activation-pop {
  0% {
    transform: scale(0.4);
    opacity: 0;
  }
  18% {
    transform: scale(1.05);
    opacity: 1;
  }
  28% {
    transform: scale(1);
  }
  80% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

@keyframes activation-turn-pop {
  0% {
    transform: scale(0.45);
  }
  70% {
    transform: scale(1.12);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes activation-destello-life {
  0% {
    transform: scale(0.12);
    opacity: 1;
  }
  38% {
    transform: scale(1.08);
    opacity: 1;
  }
  100% {
    transform: scale(0.22);
    opacity: 0;
  }
}

.online-duel-view.compact {
  --viewer-rail-width: 0px;
  --viewer-text-width: 168px;
  --viewer-rail: 0px;
  --viewer-rail-end: var(--viewer-text-width);
  padding: env(safe-area-inset-top, 0px) env(safe-area-inset-right, 0px)
    env(safe-area-inset-bottom, 0px) env(safe-area-inset-left, 0px);
}

.online-duel-view.compact.viewer-stowed {
  --viewer-rail-end: 88px;
}

.online-duel-view.picker-covers-rail,
.online-duel-view.compact.picker-covers-rail {
  --viewer-rail-width: 0px;
  --viewer-text-width: 0px;
  --viewer-rail: 0px;
  --viewer-rail-end: 0px;
}

.viewer-thumb-tools :deep(.compact-trap-chip.mode-chip) {
  min-width: 0;
  width: auto;
  min-height: 0;
  padding: 1px 4px;
  border-radius: 6px;
}

.viewer-thumb-tools :deep(.mode-kicker) {
  font-size: 7px;
  letter-spacing: 0.04em;
}

.viewer-thumb-tools :deep(.mode-value) {
  font-size: 10px;
  line-height: 1.1;
}

.compact-gear,
.compact-cancel {
  pointer-events: auto;
  position: relative;
  z-index: 1;
  min-width: 28px;
  min-height: 26px;
  padding: 0 4px;
  border-radius: 6px;
  border: 1px solid rgba(232, 197, 71, 0.4);
  background: #0c0e18;
  color: #fde68a;
  font-size: 14px;
  cursor: pointer;
  touch-action: manipulation;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.compact-gear.compact-gear-corner {
  position: absolute;
  top: 6px;
  left: 6px;
  z-index: 210;
  box-sizing: border-box;
  width: 26px;
  height: 26px;
  min-width: 0;
  min-height: 0;
  padding: 0;
  font-size: 16px;
  line-height: 1;
}

.compact-gear:disabled {
  opacity: 0.45;
  cursor: default;
}

.compact-cancel {
  font-size: 11px;
  font-weight: 600;
}

.compact-gear-x {
  display: block;
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
}

.compact-gear-backdrop {
  position: absolute;
  inset: 0;
  z-index: 185;
  background: rgba(15, 23, 42, 0.4);
}

.compact-gear-panel {
  position: absolute;
  top: 36px;
  left: 6px;
  right: auto;
  z-index: 190;
  width: min(280px, calc(100% - 16px));
  max-height: calc(100% - 16px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  border-radius: 10px;
  background: rgba(8, 10, 18, 0.96);
  border: 1px solid rgba(232, 197, 71, 0.28);
}

.compact-gear-panel :deep(.duel-settings) {
  max-height: none;
  overflow: visible;
  padding: 0;
}

.compact-log-title {
  flex: 0 0 auto;
  margin: 4px 4px 0;
  color: #fde68a;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.compact-log-host {
  /* The gear sheet is a height-capped flex column. overflow:hidden would
     otherwise make this child's minimum size 0, so on a short landscape
     phone the play list collapses and only the Log heading remains. */
  flex: 0 0 auto;
  height: min(28vh, 220px);
  min-height: 120px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.55);
  touch-action: pan-y;
}

.duel-shuffle-hand {
  position: fixed;
  right: calc(max(16px, env(safe-area-inset-right, 0px)) + 132px);
  bottom: max(16px, env(safe-area-inset-bottom, 0px));
  z-index: 210;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--md-sys-color-outline-variant);
  background: var(--md-sys-color-surface-container);
  color: var(--md-sys-color-on-surface);
  font-family: Roboto, system-ui, sans-serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.03em;
  cursor: pointer;
  pointer-events: auto;
  transition:
    border-color var(--md-motion-short) var(--md-motion-standard),
    color var(--md-motion-short) var(--md-motion-standard),
    background-color var(--md-motion-short) var(--md-motion-standard);
}

.duel-shuffle-hand:hover:not(:disabled) {
  border-color: var(--md-sys-color-primary);
  color: var(--md-sys-color-primary);
}

.duel-shuffle-hand:disabled {
  opacity: 0.45;
  cursor: default;
}

.shuffle-hand-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (prefers-reduced-motion: reduce) {
  .duel-shuffle-hand {
    transition: none;
  }
}

</style>
